import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Space,
  Button,
  Table,
  Tag,
  Tooltip,
  Modal,
  Popconfirm,
  Form,
  Input,
  Select,
  message,
  Tabs,
  Badge,
  Alert,
  Divider,
  Drawer,
  Radio,
  Segmented,
  Empty,
  List,
  Avatar,
  Statistic,
  Descriptions,
  Progress,
  Steps,
  Spin,
  Switch,
  Slider,
  InputNumber,
  Collapse,
  Timeline,
  Result,
  Breadcrumb,
  Layout,
  Checkbox,
} from 'antd';
import '../Generation/step1/generation.css';
import { getUserProfile } from '../../../auth';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CloudUploadOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  SearchOutlined,
  BarsOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  BarChartOutlined,
  ExportOutlined,
  ImportOutlined,
  LinkOutlined,
  FileTextOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  GlobalOutlined,
  CopyOutlined,
  SaveOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  CheckOutlined,
  WarningOutlined,
  QuestionCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
  SyncOutlined,
  FolderOpenOutlined,
  FileSearchOutlined,
  FormatPainterOutlined,
  HighlightOutlined,
  FontSizeOutlined,
  AlignLeftOutlined,
  OrderedListOutlined,
  TagsOutlined,
  UserOutlined,
  TeamOutlined,
  CommentOutlined,
  HeartOutlined,
  StarOutlined,
  FireOutlined,
  RocketOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { v2StylesApi, api, gamificationApi } from '../../../api';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { LockOutlined } from '@ant-design/icons';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import TipsRotator from '../../Common/TipsRotator';
import { styleTips } from '../../Common/tipsData';
import EnhancedTour, { TourHelpButton } from '../../Common/EnhancedTour';
import { TOUR_CONFIGS } from '../../../config/tourConfigs';
import { useTutorialTour } from '../../../hooks/useTutorialTour';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Option } = Select;
const { confirm } = Modal;
const { Step } = Steps;
const { Panel } = Collapse;

// 接続詞マスターリスト（30種類・カテゴリ別）
const CONNECTIVE_WORDS = {
  reversal: {
    label: '逆接',
    words: [
      'しかし',
      'だが',
      'ただし',
      'もっとも',
      'とはいえ',
      '一方で',
      'それでも',
    ],
  },
  causality: {
    label: '順接・因果',
    words: [
      'そのため',
      'したがって',
      'ゆえに',
      'だから',
      'その結果',
      'それによって',
    ],
  },
  addition: {
    label: '並列・追加',
    words: ['また', 'さらに', '加えて', 'そして', '同様に'],
  },
  example: {
    label: '例示',
    words: ['例えば', '具体的には', 'たとえば'],
  },
  sequence: {
    label: '展開・転換',
    words: ['まず', '次に', '最後に', 'ちなみに'],
  },
  summary: {
    label: '要約・結論',
    words: ['つまり', '要するに', '結論として'],
  },
  supplement: {
    label: '補足・説明',
    words: ['なお', 'ただ'],
  },
};

// 全接続詞のフラットリスト
const ALL_CONNECTIVE_WORDS = Object.values(CONNECTIVE_WORDS).flatMap(
  cat => cat.words,
);

// スタイル項目の定義
const styleCategories = {
  tone: {
    title: 'トーン・文体',
    icon: <FontSizeOutlined />,
    items: {
      formality: { label: 'フォーマル度', type: 'slider', min: 0, max: 100 },
      friendliness: { label: '親しみやすさ', type: 'slider', min: 0, max: 100 },
      expertise: { label: '専門性', type: 'slider', min: 0, max: 100 },
      enthusiasm: { label: '熱量', type: 'slider', min: 0, max: 100 },
      sentence_length: {
        label: 'センテンスの長さ',
        type: 'select',
        options: ['短め', '標準', '長め'],
      },
      vocabulary: {
        label: '語彙レベル',
        type: 'select',
        options: ['平易', '標準', '専門的'],
      },
    },
  },
  position: {
    title: '筆者の立ち位置',
    icon: <UserOutlined />,
    items: {
      perspective: {
        label: '視点',
        type: 'select',
        options: ['第三者', '専門家', '経験者', '初心者'],
      },
      authority: { label: '権威性の演出', type: 'slider', min: 0, max: 100 },
      objectivity: { label: '客観性', type: 'slider', min: 0, max: 100 },
      personality: { label: '個性の表出', type: 'slider', min: 0, max: 100 },
    },
  },
  structure: {
    title: '構造・見出し',
    icon: <OrderedListOutlined />,
    items: {
      heading_style: {
        label: '見出しスタイル',
        type: 'select',
        options: ['疑問形', '断定形', '提案形', 'ハイブリッド'],
      },
      heading_length: {
        label: '見出しの長さ',
        type: 'select',
        options: ['短い', '標準', '長い'],
      },
      section_depth: {
        label: 'セクション階層',
        type: 'number',
        min: 2,
        max: 4,
      },
      intro_style: {
        label: '導入部スタイル',
        type: 'select',
        options: ['問題提起', '結論先行', '共感型', 'データ提示'],
      },
      conclusion_style: {
        label: 'まとめスタイル',
        type: 'select',
        options: ['要約型', 'CTA型', '展望型', 'Q&A型'],
      },
    },
  },
  promotion: {
    title: 'サービス訴求',
    icon: <FireOutlined />,
    items: {
      promotion_frequency: {
        label: '訴求頻度',
        type: 'slider',
        min: 0,
        max: 100,
      },
      promotion_style: {
        label: '訴求スタイル',
        type: 'select',
        options: ['直接的', '間接的', 'ストーリー型', 'データ型'],
      },
      cta_position: {
        label: 'CTA配置',
        type: 'select',
        options: ['冒頭', '中盤', '末尾', '複数箇所'],
      },
      urgency: { label: '緊急性の演出', type: 'slider', min: 0, max: 100 },
    },
  },
  engagement: {
    title: 'エンゲージメント',
    icon: <HeartOutlined />,
    items: {
      question_frequency: {
        label: '問いかけ頻度',
        type: 'slider',
        min: 0,
        max: 100,
      },
      example_usage: { label: '例示の使用', type: 'slider', min: 0, max: 100 },
      data_presentation: {
        label: 'データ提示',
        type: 'slider',
        min: 0,
        max: 100,
      },
      visual_elements: {
        label: '視覚要素の活用',
        type: 'slider',
        min: 0,
        max: 100,
      },
    },
  },
};

// URL/ドメインからドメイン部分のみを抽出するヘルパー
const extractDomainFromLine = (line) => {
  const trimmed = (line || '').trim();
  if (!trimmed) return '';
  
  // TLD指定（.gov, .edu など）はそのまま
  if (/^\.[a-z]{2,}$/i.test(trimmed)) return trimmed;
  
  // URLパターンの場合、ドメインを抽出
  try {
    if (trimmed.includes('://') || trimmed.startsWith('www.')) {
      const urlStr = trimmed.startsWith('www.') ? `https://${trimmed}` : trimmed;
      const url = new URL(urlStr);
      return url.hostname.replace(/^www\./, '');
    }
  } catch (_) {
    // URL解析失敗時はそのまま
  }
  
  // パス付きドメイン（example.com/path）の場合
  const slashIdx = trimmed.indexOf('/');
  if (slashIdx > 0) {
    return trimmed.substring(0, slashIdx).replace(/^www\./, '');
  }
  
  // それ以外はそのまま返す
  return trimmed.replace(/^www\./, '');
};

// 複数行のテキストをドメインリストに変換
const processDomainInput = (text) => {
  if (!text) return '';
  return text
    .split('\n')
    .map(line => extractDomainFromLine(line))
    .filter(d => d)
    .join('\n');
};

const StyleManagement = () => {
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [yamlModalVisible, setYamlModalVisible] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [currentStyle, setCurrentStyle] = useState(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [configForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('card');
  const [currentStep, setCurrentStep] = useState(0);
  const [analyzingUrl, setAnalyzingUrl] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [yamlContent, setYamlContent] = useState('');
  const [yamlPreview, setYamlPreview] = useState('');
  const [yamlLoading, setYamlLoading] = useState(false);
  const [yamlPreviewOpen, setYamlPreviewOpen] = useState(false);
  const [draftConfig, setDraftConfig] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // 接続詞の3段階管理（forbidden/minimize/moderate）
  const [forbiddenWords, setForbiddenWords] = useState([]);
  const [minimizeWords, setMinimizeWords] = useState([]);
  const [moderateWords, setModerateWords] = useState([]);

  // 機能制御用の状態
  const [featureStatus, setFeatureStatus] = useState({
    unlocked: false,
    currentCount: 0,
    maxLimit: 0,
    requiredPlan: '',
  });
  const [featureLoading, setFeatureLoading] = useState(true);

  // ツアー管理（スタイル管理用）
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep: tourCurrentStep,
    updateCurrentStep: updateTourCurrentStep,
    loading: tourLoading,
  } = useTutorialTour('style_management');

  // サーバでYAML生成（style_config→YAML）
  const openYamlModal = async record => {
    try {
      const cfg = (record && record.style_config) || {};
      const res = await v2StylesApi.convertYaml(cfg);
      setYamlContent(res?.yaml || '');
      setYamlModalVisible(true);
    } catch (e) {
      message.error('YAML生成に失敗しました');
    }
  };

  // ネスト操作ユーティリティ
  const getNested = (obj, path, defaultValue = undefined) => {
    try {
      return (
        path.split('.').reduce((o, k) => (o || {})[k], obj) ?? defaultValue
      );
    } catch (_) {
      return defaultValue;
    }
  };
  // v1/v2両対応のため、複数候補パスから最初に見つかった値を返す
  const pickFirst = (obj, paths, defaultValue = undefined) => {
    try {
      for (const p of paths) {
        const v = getNested(obj, p, undefined);
        if (v !== undefined) return v;
      }
      return defaultValue;
    } catch (_) {
      return defaultValue;
    }
  };
  const setNested = (obj, path, value) => {
    const keys = path.split('.');
    const newObj = { ...obj };
    let cur = newObj;
    for (let i = 0; i < keys.length - 1; i += 1) {
      const k = keys[i];
      const next = cur[k];
      cur[k] = Array.isArray(next)
        ? [...next]
        : typeof next === 'object' && next !== null
          ? { ...next }
          : {};
      cur = cur[k];
    }
    cur[keys[keys.length - 1]] = value;
    return newObj;
  };

  // 数値レンジ文字列 "a-b" から中央値を返すユーティリティ（存在しない/不正は NaN）
  const getRangeMid = s => {
    if (!s) return NaN;
    const parts = String(s)
      .split('-')
      .map(x => Number(String(x).trim()));
    if (parts.length >= 2) {
      const a = Number.isFinite(parts[0]) ? parts[0] : NaN;
      const b = Number.isFinite(parts[1]) ? parts[1] : a;
      if (!Number.isFinite(a) && !Number.isFinite(b)) return NaN;
      const lo =
        Number.isFinite(a) && Number.isFinite(b)
          ? Math.min(a, b)
          : Number.isFinite(a)
            ? a
            : b;
      const hi =
        Number.isFinite(a) && Number.isFinite(b)
          ? Math.max(a, b)
          : Number.isFinite(a)
            ? a
            : b;
      return (lo + hi) / 2;
    }
    const v = Number(parts[0]);
    return Number.isFinite(v) ? v : NaN;
  };

  // 7段階プリセット ←→ レンジのマッピング
  const mapBulletsPreset = s => {
    const mid = getRangeMid(s);
    if (!Number.isFinite(mid)) return 'medium';
    if (mid < 1) return 'very_low'; // 0-1
    if (mid < 1.5) return 'low'; // 1-2
    if (mid < 2.5) return 'slightly_low'; // 2-3
    if (mid <= 3) return 'medium'; // 2-4 (mid=3)
    if (mid <= 5) return 'slightly_high'; // 4-6 (mid=5)
    if (mid <= 6.5) return 'high'; // 5-8 (mid=6.5)
    return 'very_high'; // 6-8 (mid=7)
  };
  const mapTablesPreset = s => {
    const t = String(s || '').trim();
    let mid = NaN;
    if (/^\d+$/.test(t)) {
      mid = Number(t);
    } else if (t.includes('-')) {
      const parts = t.split('-').map(x => Number(String(x).trim()));
      if (parts.length >= 2) {
        const a = Number.isFinite(parts[0]) ? parts[0] : NaN;
        const b = Number.isFinite(parts[1]) ? parts[1] : a;
        if (Number.isFinite(a) || Number.isFinite(b)) {
          const lo =
            Number.isFinite(a) && Number.isFinite(b)
              ? Math.min(a, b)
              : Number.isFinite(a)
                ? a
                : b;
          const hi =
            Number.isFinite(a) && Number.isFinite(b)
              ? Math.max(a, b)
              : Number.isFinite(a)
                ? a
                : b;
          mid = (lo + hi) / 2;
        }
      }
    }
    if (!Number.isFinite(mid)) return 'medium';
    if (mid < 0.5) return 'very_low'; // 0-1 (mid=0.5)
    if (mid < 1.5) return 'low'; // 1-2 (mid=1.5)
    if (mid < 2) return 'slightly_low'; // 1-2 (mid=1.5)
    if (mid <= 2.5) return 'medium'; // 2-3 (mid=2.5)
    if (mid <= 3.5) return 'slightly_high'; // 3-4 (mid=3.5)
    if (mid <= 5) return 'high'; // 4-6 (mid=5)
    return 'very_high'; // 5-7 (mid=6)
  };

  // 設定モーダルを開くユーティリティ（解析結果を逆マップして初期値セット）
  const openConfigForStyle = record => {
    setCurrentStyle(record);
    const cfg = (record && record.style_config) || {};
    const freqToPreset = s => {
      const t = String(s || '').trim();
      if (/^(0-1|0–1|0—1)$/i.test(t)) return '少なめ';
      if (/^(3-5|3–5|3—5)$/i.test(t)) return '多め';
      return '標準';
    };
    const regPoliteness = pickFirst(cfg, [
      'tone.register.politeness',
      'prose_style.tone.register.politeness',
    ]);
    const regFormality = pickFirst(cfg, [
      'tone.register.formality',
      'prose_style.tone.register.formality',
    ]);
    const parseRangeMid = s => {
      if (!s) return null;
      const m = String(s).match(/(\d+)\s*-\s*(\d+)/);
      if (!m) return null;
      const a = parseInt(m[1], 10);
      const b = parseInt(m[2], 10);
      if (Number.isNaN(a) || Number.isNaN(b)) return null;
      return (a + b) / 2;
    };
    const getRangeMid = s => {
      if (!s) return NaN;
      const parts = String(s)
        .split('-')
        .map(x => Number(String(x).trim()));
      if (parts.length >= 2) {
        const a = Number.isFinite(parts[0]) ? parts[0] : NaN;
        const b = Number.isFinite(parts[1]) ? parts[1] : a;
        if (!Number.isFinite(a) && !Number.isFinite(b)) return NaN;
        const lo =
          Number.isFinite(a) && Number.isFinite(b)
            ? Math.min(a, b)
            : Number.isFinite(a)
              ? a
              : b;
        const hi =
          Number.isFinite(a) && Number.isFinite(b)
            ? Math.max(a, b)
            : Number.isFinite(a)
              ? a
              : b;
        return (lo + hi) / 2;
      }
      const v = Number(parts[0]);
      return Number.isFinite(v) ? v : NaN;
    };
    const desuMasuRatio = parseRangeMid(
      pickFirst(cfg, [
        'prose_style.sentence_endings.ratios_per_100_sentences.desu_masu',
        'sentence_endings.ratios_per_100_sentences.desu_masu',
      ]),
    );
    let politenessInit = 30;
    if (regPoliteness === 'です/ます' || regPoliteness === '丁寧') {
      politenessInit = 80;
    } else if (regFormality === '中程度' || regFormality === 'medium') {
      politenessInit = 55;
    } else if (typeof desuMasuRatio === 'number') {
      politenessInit = desuMasuRatio >= 85 ? 80 : desuMasuRatio >= 60 ? 55 : 30;
    }
    const sentAvg = String(
      pickFirst(cfg, [
        'rhythm.sentence_length_chars.avg',
        'prose_style.rhythm.sentence_length_chars.avg',
      ]) || '35-55',
    );
    const sentenceInit = /25-40/.test(sentAvg)
      ? 30
      : /50-70/.test(sentAvg)
        ? 75
        : 55;
    const toneIntensity = pickFirst(cfg, [
      'tone.intensity',
      'prose_style.tone.intensity',
    ]);
    const tonePrimary = pickFirst(cfg, [
      'tone.primary',
      'prose_style.tone.primary',
    ]);
    let energyInit = 50;
    if (Number(toneIntensity)) {
      energyInit = Number(toneIntensity);
    } else if (Array.isArray(tonePrimary) && tonePrimary.includes('推進的')) {
      energyInit = 70;
    } else {
      const hsTemp =
        getNested(cfg, 'headings_style.temperature') ||
        getNested(cfg, 'headings.temperature') ||
        {};
      const aH2 = Number(hsTemp?.assertiveness?.h2);
      const aH3 = Number(hsTemp?.assertiveness?.h3);
      if (Number.isFinite(aH2) || Number.isFinite(aH3)) {
        const avg = Math.round(((aH2 || aH3 || 0) + (aH3 || aH2 || 0)) / 2);
        energyInit = Math.max(0, Math.min(100, avg));
      } else {
        const tf = getNested(
          cfg,
          'prose_style.transitions.frequency_per_1000w.typical',
        );
        if (typeof tf === 'string') {
          const parts = tf
            .split('-')
            .map(x => Number(String(x).trim()))
            .filter(n => Number.isFinite(n));
          if (parts.length) {
            const mid =
              parts.length >= 2 ? (parts[0] + parts[1]) / 2 : parts[0];
            energyInit = Math.max(0, Math.min(100, Math.round(mid * 10)));
          }
        }
      }
    }
    const infoRaw = pickFirst(cfg, [
      'rhythm.info_density',
      'prose_style.rhythm.info_density',
    ]);
    let infoInit = Number(infoRaw) || 0;
    const neutralityInit =
      Number(
        pickFirst(cfg, ['tone.neutrality', 'prose_style.tone.neutrality']),
      ) || 50;
    const expertiseRaw = pickFirst(cfg, [
      'tone.expertise',
      'prose_style.tone.expertise',
    ]);
    let expertiseInit = Number(expertiseRaw) || 0;
    // 欠損時の近似: 語彙性はセンテンスの長さから、専門性は(100 - 中立性)
    if (!Number.isFinite(infoInit) || infoInit <= 0) {
      infoInit = /25-40/.test(sentAvg) ? 30 : /50-70/.test(sentAvg) ? 75 : 55;
    }
    if (!Number.isFinite(expertiseInit) || expertiseInit <= 0) {
      const n = Number.isFinite(neutralityInit) ? neutralityInit : 50;
      expertiseInit = Math.max(0, Math.min(100, 100 - n));
    }
    const transPreset = freqToPreset(
      getNested(cfg, 'rhythm.transitions.frequency_per_1000w.typical'),
    );
    const linkPreset = freqToPreset(
      getNested(cfg, 'references.link_density_per_1000w.typical'),
    );
    const bulletsPresetInit = mapBulletsPreset(
      pickFirst(cfg, [
        'prose_style.formatting.bullets.frequency_per_1000w.typical',
        'formatting.bullets.frequency_per_1000w.typical',
      ]),
    );
    const tablesPresetInit = mapTablesPreset(
      pickFirst(cfg, [
        'prose_style.formatting.tables.frequency_per_1000w.typical',
        'formatting.tables.frequency_per_1000w.typical',
      ]),
    );
    const avoidLines = (
      getNested(cfg, 'prose_style.constraints.avoid_patterns', []) ||
      getNested(cfg, 'constraints.avoid_patterns', []) ||
      []
    ).join('\n');
    const rewriteLines = (
      getNested(cfg, 'prose_style.constraints.rewrite_rules', []) ||
      getNested(cfg, 'constraints.rewrite_rules', []) ||
      []
    )
      .map(r => `${(r && r.from) || ''}=>${(r && r.to) || ''}`.trim())
      .filter(Boolean)
      .join('\n');
    const additionalInstructionsOutline =
      getNested(
        cfg,
        'prose_style.constraints.additional_instructions_outline',
        '',
      ) || getNested(cfg, 'constraints.additional_instructions_outline', '');
    const additionalInstructionsContent =
      getNested(
        cfg,
        'prose_style.constraints.additional_instructions_content',
        '',
      ) ||
      getNested(cfg, 'constraints.additional_instructions_content', '') ||
      // 旧形式との互換性（単一のadditional_instructions）
      getNested(cfg, 'prose_style.constraints.additional_instructions', '') ||
      getNested(cfg, 'constraints.additional_instructions', '');

    configForm.setFieldsValue({
      name: record?.name,
      description: record?.description,
      language: getNested(cfg, 'metadata.language'),
      talc_min: getNested(cfg, 'metadata.typical_article_length_chars.min'),
      talc_max: getNested(cfg, 'metadata.typical_article_length_chars.max'),
      talc_pref: getNested(
        cfg,
        'metadata.typical_article_length_chars.preference',
      ),
      allowed_levels: pickFirst(
        cfg,
        ['headings.allowed_levels', 'headings_style.allowed_levels'],
        [],
      ),
      depth_min: pickFirst(cfg, [
        'headings.depth.min',
        'headings_style.depth.min',
      ]),
      depth_max: pickFirst(cfg, [
        'headings.depth.max',
        'headings_style.depth.max',
      ]),
      h2_typical: pickFirst(cfg, [
        'headings.h2_per_article.typical',
        'headings_style.counts.h2_per_article.typical',
      ]),
      h2_upper: getNested(cfg, 'headings.h2_per_article.upper_hard_limit'),
      h3_typical: pickFirst(cfg, [
        'headings.h3_per_h2.typical',
        'headings_style.counts.h3_per_h2.typical',
      ]),
      // legacy dense mode は廃止
      trans_freq: transPreset,
      link_density: linkPreset,
      bullets_preset: bulletsPresetInit,
      tables_preset: tablesPresetInit,
      code_inline_allowed: pickFirst(cfg, [
        'prose_style.formatting.emphasis.code_inline_allowed',
        'formatting.emphasis.code_inline_allowed',
      ]),
      politeness: politenessInit,
      sentence_length: sentenceInit,
      energy: energyInit,
      info_density: infoInit,
      neutrality: neutralityInit,
      expertise: expertiseInit,
      avoid_text: avoidLines,
      rewrite_text: rewriteLines,
      additional_instructions_outline: additionalInstructionsOutline,
      additional_instructions_content: additionalInstructionsContent,
    });
    // デバッグ: 単一オブジェクトで出力
    try {
      const initialFormDump = configForm.getFieldsValue(true);
      console.log({
        tag: '[Style Config Debug] openConfigForStyle',
        record_id: record?.id,
        raw_style_config: cfg,
        mapped_initial_form: initialFormDump,
      });
    } catch (_) {
      // no-op
    }
    // 追加: 人間向けUIフィールド（疑問形/記号/見出し文字数/つなぎ言葉/作法）
    try {
      const allowInterrogative = !!pickFirst(cfg, [
        'headings_style.temperature.allow_interrogative',
        'headings.temperature.allow_interrogative',
      ]);
      const symbols = pickFirst(
        cfg,
        [
          'headings_style.temperature.allow_symbols',
          'headings.temperature.allow_symbols',
        ],
        {},
      );
      const uiSymbols = [];
      if (symbols && symbols.question) uiSymbols.push('question');
      if (symbols && symbols.brackets) uiSymbols.push('brackets');
      if (symbols && symbols.colon) uiSymbols.push('colon');
      const h2CharsTypical = pickFirst(cfg, [
        'headings_style.length_policy.h2_chars.typical',
        'headings.length_policy.h2_chars.typical',
      ]);
      const h3CharsTypical = pickFirst(cfg, [
        'headings_style.length_policy.h3_chars.typical',
        'headings.length_policy.h3_chars.typical',
      ]);
      const h2BodyCharsTypical = pickFirst(cfg, [
        'headings_style.length_policy.h2_body_chars.typical',
        'headings.length_policy.h2_body_chars.typical',
      ]);
      const h3BodyCharsTypical = pickFirst(cfg, [
        'headings_style.length_policy.h3_body_chars.typical',
        'headings.length_policy.h3_body_chars.typical',
      ]);

      // 接続詞（新形式: connective_words）
      const connectiveWords = pickFirst(
        cfg,
        [
          'prose_style.transitions.connective_words',
          'rhythm.transitions.connective_words',
        ],
        {},
      );

      const bulletsRules = pickFirst(
        cfg,
        ['prose_style.formatting.rules.bullets'],
        {},
      );
      const tablesRules = pickFirst(
        cfg,
        ['prose_style.formatting.rules.tables'],
        {},
      );
      configForm.setFieldsValue({
        ui_allow_interrogative: allowInterrogative,
        ui_heading_symbols: uiSymbols,
        h2_chars_typical: h2CharsTypical,
        h3_chars_typical: h3CharsTypical,
        h2_body_chars_typical: h2BodyCharsTypical,
        h3_body_chars_typical: h3BodyCharsTypical,
        connective_words_data: connectiveWords, // 新形式
        bullets_max_nesting: bulletsRules.max_nesting ?? 1,
        bullets_item_max_chars: bulletsRules.item_max_chars ?? 80,
        bullets_terminal_punct_disallowed:
          bulletsRules.terminal_punct_disallowed ?? true,
        bullets_parallelism_required: bulletsRules.parallelism_required ?? true,
        tables_usage: Array.isArray(tablesRules.usage)
          ? tablesRules.usage
          : ['比較', '要点整理'],
        tables_header_required: tablesRules.header_required ?? true,
        tables_caption_allowed: tablesRules.caption_allowed ?? true,
      });

      // 接続詞stateにロード
      loadConnectiveWordsFromConfig(connectiveWords);

      // 文末表現のON/OFF初期化
      const sentenceEndings = pickFirst(
        cfg,
        ['prose_style.sentence_endings', 'rhythm.sentence_endings'],
        {},
      );
      // 新形式: variants直下にboolean、旧形式との互換性を保持
      const variants = sentenceEndings.variants || {};
      const variantMix = variants.variant_mix_per_100_sentences || {}; // 旧形式

      // 基本文末（ratio > 0ならON）
      // 文末表現の解析（boolean形式に対応）
      const parseEndingEnabled = value => {
        if (typeof value === 'boolean') {
          return value;
        }
        // 旧形式（"0-0"など）からの変換
        if (!value) return false;
        if (typeof value === 'string') {
          const match = String(value).match(/(\d+)/);
          return match && parseInt(match[1], 10) > 0;
        }
        return false;
      };

      configForm.setFieldsValue({
        // variantsは直接booleanまたは旧形式のvariant_mix_per_100_sentences
        sentence_ending_desu_enabled: parseEndingEnabled(
          variants.desu ?? variantMix.desu,
        ),
        sentence_ending_masu_enabled: parseEndingEnabled(
          variants.masu ?? variantMix.masu,
        ),
        sentence_ending_masen_enabled: parseEndingEnabled(
          variants.masen ?? variantMix.masen,
        ),
        sentence_ending_deshou_enabled: parseEndingEnabled(
          variants.deshou ?? variantMix.deshou,
        ),
        sentence_ending_desune_enabled: parseEndingEnabled(
          variants.desune ?? variantMix.desune,
        ),
        sentence_ending_desuyo_enabled: parseEndingEnabled(
          variants.desuyo ?? variantMix.desuyo,
        ),
        sentence_ending_masune_enabled: parseEndingEnabled(
          variants.masune ?? variantMix.masune,
        ),
        sentence_ending_masuyo_enabled: parseEndingEnabled(
          variants.masuyo ?? variantMix.masuyo,
        ),
        sentence_ending_deshita_enabled: parseEndingEnabled(
          variants.deshita ?? variantMix.deshita,
        ),
        sentence_ending_mashita_enabled: parseEndingEnabled(
          variants.mashita ?? variantMix.mashita,
        ),
        sentence_ending_mashou_enabled: parseEndingEnabled(
          variants.mashou ?? variantMix.mashou,
        ),
        sentence_ending_desuka_enabled: parseEndingEnabled(
          variants.desuka ?? variantMix.desuka,
        ),
        sentence_ending_masuka_enabled: parseEndingEnabled(
          variants.masuka ?? variantMix.masuka,
        ),
        sentence_ending_ndesu_enabled: parseEndingEnabled(
          variants.ndesu ?? variantMix.ndesu,
        ),
        sentence_ending_masenka_enabled: parseEndingEnabled(
          variants.masenka ?? variantMix.masenka,
        ),
        sentence_ending_deshouka_enabled: parseEndingEnabled(
          variants.deshouka ?? variantMix.deshouka,
        ),
        sentence_ending_masuyoune_enabled: parseEndingEnabled(
          variants.masuyoune ?? variantMix.masuyoune,
        ),
        sentence_ending_desuyoune_enabled: parseEndingEnabled(
          variants.desuyoune ?? variantMix.desuyoune,
        ),
        sentence_ending_to_omoimasu_enabled: parseEndingEnabled(
          variants.to_omoimasu,
        ),
        sentence_ending_kudasai_enabled: parseEndingEnabled(variants.kudasai),
        sentence_ending_shimashou_enabled: parseEndingEnabled(
          variants.shimashou,
        ),
      });
    } catch (_) {
      // noop for UI extras
    }
    // 記事長の初期推定（未設定時のみ）
    try {
      const talcMin = getNested(
        cfg,
        'metadata.typical_article_length_chars.min',
      );
      const talcMax = getNested(
        cfg,
        'metadata.typical_article_length_chars.max',
      );
      const talcPref = getNested(
        cfg,
        'metadata.typical_article_length_chars.preference',
      );
      const parseRangeMid = s => {
        if (!s) return null;
        const m = String(s).match(/(\d+)\s*-\s*(\d+)/);
        if (!m) return null;
        const a = parseInt(m[1], 10);
        const b = parseInt(m[2], 10);
        if (Number.isNaN(a) || Number.isNaN(b)) return null;
        return (a + b) / 2;
      };
      const parseAvgInt = s => {
        const m = parseRangeMid(s);
        return typeof m === 'number' ? Math.round(m) : null;
      };
      const h2CountAvg = parseRangeMid(
        pickFirst(cfg, [
          'headings_style.counts.h2_per_article.typical',
          'headings.h2_per_article.typical',
        ]),
      );
      const h3PerH2Avg = parseRangeMid(
        pickFirst(cfg, [
          'headings_style.counts.h3_per_h2.typical',
          'headings.h3_per_h2.typical',
        ]),
      );
      const h2BodyAvg = parseAvgInt(
        pickFirst(cfg, [
          'headings_style.length_policy.h2_body_chars.typical',
          'headings.length_policy.h2_body_chars.typical',
        ]),
      );
      const h3BodyAvg = parseAvgInt(
        pickFirst(cfg, [
          'headings_style.length_policy.h3_body_chars.typical',
          'headings.length_policy.h3_body_chars.typical',
        ]),
      );
      if (!talcMin && !talcMax && !talcPref && h2CountAvg !== null) {
        const h3PerH2 = h3PerH2Avg ?? 0;
        const h2Body = h2BodyAvg ?? 900;
        const h3Body = h3BodyAvg ?? 400;
        const estimated = Math.round(
          h2CountAvg * (h2Body + h3PerH2 * h3Body) * 1.1,
        );
        const minEst = Math.max(0, Math.floor(estimated * 0.85));
        const maxEst = Math.max(minEst, Math.ceil(estimated * 1.15));
        configForm.setFieldsValue({
          talc_min: minEst,
          talc_max: maxEst,
          talc_pref: `${minEst}-${maxEst}`,
        });
      }
    } catch (_) {
      // no-op for estimation
    }

    // 調査フィルター初期化
    try {
      const domainFilter = getNested(cfg, 'domain_filter') || {};
      configForm.setFieldsValue({
        domain_filter_mode: domainFilter.mode || 'disabled',
        domain_filter_whitelist: Array.isArray(domainFilter.whitelist) 
          ? domainFilter.whitelist.join('\n') 
          : '',
        domain_filter_blacklist: Array.isArray(domainFilter.blacklist) 
          ? domainFilter.blacklist.join('\n') 
          : '',
      });
    } catch (_) {
      // no-op for domain filter
    }

    setConfigModalVisible(true);
    try {
      const cfg = (record && record.style_config) || {};
      setDraftConfig(JSON.parse(JSON.stringify(cfg)));
    } catch (_) {
      setDraftConfig(record?.style_config || {});
    }
    setTimeout(() => {
      try {
        refreshYamlPreview();
      } catch (e) {
        // ignore preview refresh error
      }
    }, 0);
  };

  // 接続詞の3段階連動ハンドラー
  const handleForbiddenWordsChange = selectedWords => {
    setForbiddenWords(selectedWords);
    // 他のグループから削除
    setMinimizeWords(prev => prev.filter(w => !selectedWords.includes(w)));
    setModerateWords(prev => prev.filter(w => !selectedWords.includes(w)));
    // フォームにも反映（後で保存時に使う）
    updateConnectiveWordsInForm(selectedWords, minimizeWords, moderateWords);
  };

  const handleMinimizeWordsChange = selectedWords => {
    setMinimizeWords(selectedWords);
    // 他のグループから削除
    setForbiddenWords(prev => prev.filter(w => !selectedWords.includes(w)));
    setModerateWords(prev => prev.filter(w => !selectedWords.includes(w)));
    updateConnectiveWordsInForm(forbiddenWords, selectedWords, moderateWords);
  };

  const handleModerateWordsChange = selectedWords => {
    setModerateWords(selectedWords);
    // 他のグループから削除
    setForbiddenWords(prev => prev.filter(w => !selectedWords.includes(w)));
    setMinimizeWords(prev => prev.filter(w => !selectedWords.includes(w)));
    updateConnectiveWordsInForm(forbiddenWords, minimizeWords, selectedWords);
  };

  // フォームのconnective_wordsフィールドを更新（オブジェクト形式）
  const updateConnectiveWordsInForm = (forbidden, minimize, moderate) => {
    const connectiveWordsObj = {};
    forbidden.forEach(w => (connectiveWordsObj[w] = 'forbidden'));
    minimize.forEach(w => (connectiveWordsObj[w] = 'minimize'));
    moderate.forEach(w => (connectiveWordsObj[w] = 'moderate'));

    // hidden fieldに保存（後でrefreshYamlPreviewで使う）
    configForm.setFieldsValue({ connective_words_data: connectiveWordsObj });
  };

  // DB/APIから読み込んだconnective_wordsをstateに反映
  const loadConnectiveWordsFromConfig = connectiveWordsObj => {
    const forbidden = [];
    const minimize = [];
    const moderate = [];

    Object.entries(connectiveWordsObj || {}).forEach(([word, status]) => {
      if (status === 'forbidden') forbidden.push(word);
      else if (status === 'minimize') minimize.push(word);
      else if (status === 'moderate') moderate.push(word);
    });

    setForbiddenWords(forbidden);
    setMinimizeWords(minimize);
    setModerateWords(moderate);
  };

  // 設定フォームからプレビュー用style_configを生成してYAMLに変換
  const refreshYamlPreview = async () => {
    try {
      if (!currentStyle) return;
      const values = configForm.getFieldsValue();
      const base = draftConfig || currentStyle.style_config || {};
      let updated = { ...base };

      const mapPreset = (preset, low, mid, high) => {
        if (preset === '少なめ') return low;
        if (preset === '多め') return high;
        return mid; // 標準
      };

      // 基本・メタ（言語、説明文）
      updated = setNested(updated, 'metadata.language', values.language);
      // descriptionを反映（空文字列でも保存）
      if (typeof values.description === 'string') {
        updated = setNested(
          updated,
          'metadata.description',
          values.description,
        );
      }

      // 記事長（min/max/pref）を見出し個数・文字数から算定
      try {
        const parseNumRange = s => {
          if (!s) return [NaN, NaN, NaN];
          const parts = String(s)
            .split('-')
            .map(x => Number(String(x).trim()));
          if (parts.length >= 2) {
            const a = Number.isFinite(parts[0]) ? parts[0] : NaN;
            const b = Number.isFinite(parts[1]) ? parts[1] : a;
            const lo = Math.min(a, b);
            const hi = Math.max(a, b);
            const mid =
              Number.isFinite(lo) && Number.isFinite(hi)
                ? Math.round((lo + hi) / 2)
                : Number.isFinite(lo)
                  ? lo
                  : Number.isFinite(hi)
                    ? hi
                    : NaN;
            return [lo, hi, mid];
          }
          const v = Number(parts[0]);
          return [v, v, v];
        };
        const [h2Min, h2Max, h2Mid] = parseNumRange(values.h2_typical);
        const [h3Min, h3Max, h3Mid] = parseNumRange(values.h3_typical);
        const [h2bMin, h2bMax, h2bMid] = parseNumRange(
          values.h2_body_chars_typical,
        );
        const [h3bMin, h3bMax, h3bMid] = parseNumRange(
          values.h3_body_chars_typical,
        );

        if (
          Number.isFinite(h2Min) &&
          Number.isFinite(h2Max) &&
          Number.isFinite(h2bMin) &&
          Number.isFinite(h2bMax)
        ) {
          const ratioMaxUse = 0.8;
          const minEstBase =
            h2Min *
            (h2bMin +
              (Number.isFinite(h3Mid) ? h3Mid : 0) *
                (Number.isFinite(h3bMid) ? h3bMid : 0));
          const maxEstBase =
            h2Max *
            (h2bMax +
              ratioMaxUse *
                (Number.isFinite(h3Max) ? h3Max : 0) *
                (Number.isFinite(h3bMax) ? h3bMax : 0));
          const minEst = Math.max(0, Math.round(minEstBase / 100) * 100);
          const maxEst = Math.max(minEst, Math.round(maxEstBase / 100) * 100);
          const pref = Math.round((minEst + maxEst) / 2);
          updated = setNested(
            updated,
            'metadata.typical_article_length_chars.min',
            minEst,
          );
          updated = setNested(
            updated,
            'metadata.typical_article_length_chars.max',
            maxEst,
          );
          updated = setNested(
            updated,
            'metadata.typical_article_length_chars.preference',
            pref,
          );
        }
      } catch (_) {
        // 推定失敗時は無変更
      }

      // 見出し（v2専用）
      updated = setNested(
        updated,
        'headings_style.allowed_levels',
        values.allowed_levels || [],
      );
      if (typeof values.depth_min === 'number') {
        updated = setNested(
          updated,
          'headings_style.depth.min',
          values.depth_min,
        );
      }
      if (typeof values.depth_max === 'number') {
        updated = setNested(
          updated,
          'headings_style.depth.max',
          values.depth_max,
        );
      }
      if (values.h2_typical) {
        updated = setNested(
          updated,
          'headings_style.counts.h2_per_article.typical',
          values.h2_typical,
        );
      }
      // H2上限はv2未定義のため保存しない
      if (values.h3_typical) {
        updated = setNested(
          updated,
          'headings_style.counts.h3_per_h2.typical',
          values.h3_typical,
        );
      }

      // 見出し（疑問形/記号/文字数） - v2優先 + v1互換
      if (typeof values.ui_allow_interrogative !== 'undefined') {
        updated = setNested(
          updated,
          'headings_style.temperature.allow_interrogative',
          !!values.ui_allow_interrogative,
        );
      }
      if (Array.isArray(values.ui_heading_symbols)) {
        const sym = {
          question: values.ui_heading_symbols.includes('question'),
          brackets: values.ui_heading_symbols.includes('brackets'),
          colon: values.ui_heading_symbols.includes('colon'),
        };
        updated = setNested(
          updated,
          'headings_style.temperature.allow_symbols',
          sym,
        );
      }
      if (values.h2_chars_typical) {
        updated = setNested(
          updated,
          'headings_style.length_policy.h2_chars.typical',
          values.h2_chars_typical,
        );
      }
      if (values.h3_chars_typical) {
        updated = setNested(
          updated,
          'headings_style.length_policy.h3_chars.typical',
          values.h3_chars_typical,
        );
      }
      if (values.h2_body_chars_typical) {
        updated = setNested(
          updated,
          'headings_style.length_policy.h2_body_chars.typical',
          values.h2_body_chars_typical,
        );
      }
      if (values.h3_body_chars_typical) {
        updated = setNested(
          updated,
          'headings_style.length_policy.h3_body_chars.typical',
          values.h3_body_chars_typical,
        );
      }

      // 難解セクションはUIから除去。指定がある場合のみ反映
      if (typeof values.trans_freq !== 'undefined') {
        const transFreq = mapPreset(values.trans_freq, '2-3', '4-6', '7-9');
        updated = setNested(
          updated,
          'prose_style.transitions.frequency_per_1000w.typical',
          transFreq,
        );
      }
      // link_density はv2最適化では出力しない
      if (typeof values.code_inline_allowed !== 'undefined') {
        updated = setNested(
          updated,
          'prose_style.formatting.emphasis.code_inline_allowed',
          !!values.code_inline_allowed,
        );
      }

      // フォーマット（新規: 箇条書き/表 の頻度プリセット）
      if (typeof values.bullets_preset !== 'undefined') {
        const bulletsFreq = bulletsPresetToRange(values.bullets_preset);
        updated = setNested(
          updated,
          'prose_style.formatting.bullets.frequency_per_1000w.typical',
          bulletsFreq,
        );
      }
      if (typeof values.tables_preset !== 'undefined') {
        const tablesFreq = tablesPresetToRange(values.tables_preset);
        updated = setNested(
          updated,
          'prose_style.formatting.tables.frequency_per_1000w.typical',
          tablesFreq,
        );
      }

      // つなぎ言葉（connective_words: 3段階管理）
      if (
        values.connective_words_data &&
        typeof values.connective_words_data === 'object'
      ) {
        updated = setNested(
          updated,
          'prose_style.transitions.connective_words',
          values.connective_words_data,
        );
      }

      // フォーマットの作法ルール
      const bulletRules = {
        max_nesting:
          typeof values.bullets_max_nesting === 'number'
            ? values.bullets_max_nesting
            : 1,
        item_max_chars:
          typeof values.bullets_item_max_chars === 'number'
            ? values.bullets_item_max_chars
            : 80,
        terminal_punct_disallowed: !!values.bullets_terminal_punct_disallowed,
        parallelism_required: !!values.bullets_parallelism_required,
      };
      updated = setNested(
        updated,
        'prose_style.formatting.rules.bullets',
        bulletRules,
      );
      const tableRules = {
        usage: Array.isArray(values.tables_usage)
          ? values.tables_usage
          : ['比較', '要点整理'],
        header_required: !!values.tables_header_required,
        caption_allowed: !!values.tables_caption_allowed,
      };
      updated = setNested(
        updated,
        'prose_style.formatting.rules.tables',
        tableRules,
      );

      // 文体（スライダー: 0-100）→ 段階的にマップ
      if (typeof values.politeness === 'number') {
        const p = values.politeness;
        const politenessStr = p <= 33 ? '常体' : p >= 67 ? 'です/ます' : '混合';
        const formality = p <= 33 ? 'low' : p >= 67 ? 'high' : 'medium';
        updated = setNested(
          updated,
          'prose_style.tone.register.politeness',
          politenessStr,
        );
        updated = setNested(
          updated,
          'prose_style.tone.register.formality',
          formality,
        );
        // 可能なら文末比率/バリアントの目安も補正（強制はしない）
        if (p >= 67) {
          updated = setNested(
            updated,
            'prose_style.sentence_endings.ratios_per_100_sentences.desu_masu',
            '85-100',
          );
          updated = setNested(
            updated,
            'prose_style.sentence_endings.ratios_per_100_sentences.da_dearu',
            '0-10',
          );
          updated = setNested(
            updated,
            'prose_style.sentence_endings.variants.variant_mix_per_100_sentences.desu',
            '35-50',
          );
          updated = setNested(
            updated,
            'prose_style.sentence_endings.variants.variant_mix_per_100_sentences.masu',
            '35-50',
          );
        } else if (p <= 33) {
          const desuMasuMin = p <= 5 ? '0-0' : '0-30';
          updated = setNested(
            updated,
            'prose_style.sentence_endings.ratios_per_100_sentences.desu_masu',
            desuMasuMin,
          );
          updated = setNested(
            updated,
            'prose_style.sentence_endings.ratios_per_100_sentences.da_dearu',
            p <= 5 ? '90-100' : '60-100',
          );
          updated = setNested(
            updated,
            'prose_style.sentence_endings.variants.variant_mix_per_100_sentences.desu',
            '0-10',
          );
          updated = setNested(
            updated,
            'prose_style.sentence_endings.variants.variant_mix_per_100_sentences.masu',
            '0-10',
          );
        } else {
          updated = setNested(
            updated,
            'prose_style.sentence_endings.ratios_per_100_sentences.desu_masu',
            '30-70',
          );
          updated = setNested(
            updated,
            'prose_style.sentence_endings.ratios_per_100_sentences.da_dearu',
            '10-30',
          );
        }
      }
      if (typeof values.sentence_length === 'number') {
        const avg =
          values.sentence_length <= 33
            ? '25-40'
            : values.sentence_length >= 66
              ? '50-70'
              : '35-55';
        updated = setNested(
          updated,
          'prose_style.rhythm.sentence_length_chars.avg',
          avg,
        );
      }
      if (typeof values.energy === 'number') {
        // トーン強度（0-100）に応じて primary を段階反映し、数値も保持
        const e = values.energy;
        let primary;
        if (e <= 20) primary = ['説明的', '中立的', '落ち着いた'];
        else if (e <= 40) primary = ['説明的', '中立的'];
        else if (e <= 60) primary = ['説明的', '体系的'];
        else if (e <= 80) primary = ['説明的', '体系的', '推進的'];
        else primary = ['説明的', '推進的', '強調的'];
        updated = setNested(updated, 'prose_style.tone.primary', primary);
        updated = setNested(updated, 'prose_style.tone.intensity', e);
      }
      if (typeof values.info_density === 'number') {
        // 情報密度は正式項目として保持（0-100）
        updated = setNested(
          updated,
          'prose_style.rhythm.info_density',
          values.info_density,
        );
      }

      // 文末表現のON/OFF反映（boolean形式で書き込み）
      const applyEndingBoolean = (enabled, path) => {
        if (typeof enabled === 'boolean') {
          updated = setNested(updated, path, enabled);
        }
        // undefinedの場合は何もしない（既存値を保持）
      };

      // variantsは直接boolean
      applyEndingBoolean(
        values.sentence_ending_desu_enabled,
        'prose_style.sentence_endings.variants.desu',
      );
      applyEndingBoolean(
        values.sentence_ending_masu_enabled,
        'prose_style.sentence_endings.variants.masu',
      );
      applyEndingBoolean(
        values.sentence_ending_masen_enabled,
        'prose_style.sentence_endings.variants.masen',
      );
      applyEndingBoolean(
        values.sentence_ending_deshou_enabled,
        'prose_style.sentence_endings.variants.deshou',
      );
      applyEndingBoolean(
        values.sentence_ending_desune_enabled,
        'prose_style.sentence_endings.variants.desune',
      );
      applyEndingBoolean(
        values.sentence_ending_desuyo_enabled,
        'prose_style.sentence_endings.variants.desuyo',
      );
      applyEndingBoolean(
        values.sentence_ending_masune_enabled,
        'prose_style.sentence_endings.variants.masune',
      );
      applyEndingBoolean(
        values.sentence_ending_masuyo_enabled,
        'prose_style.sentence_endings.variants.masuyo',
      );
      applyEndingBoolean(
        values.sentence_ending_deshita_enabled,
        'prose_style.sentence_endings.variants.deshita',
      );
      applyEndingBoolean(
        values.sentence_ending_mashita_enabled,
        'prose_style.sentence_endings.variants.mashita',
      );
      applyEndingBoolean(
        values.sentence_ending_mashou_enabled,
        'prose_style.sentence_endings.variants.mashou',
      );
      applyEndingBoolean(
        values.sentence_ending_desuka_enabled,
        'prose_style.sentence_endings.variants.desuka',
      );
      applyEndingBoolean(
        values.sentence_ending_masuka_enabled,
        'prose_style.sentence_endings.variants.masuka',
      );
      applyEndingBoolean(
        values.sentence_ending_ndesu_enabled,
        'prose_style.sentence_endings.variants.ndesu',
      );
      applyEndingBoolean(
        values.sentence_ending_masenka_enabled,
        'prose_style.sentence_endings.variants.masenka',
      );
      applyEndingBoolean(
        values.sentence_ending_deshouka_enabled,
        'prose_style.sentence_endings.variants.deshouka',
      );
      applyEndingBoolean(
        values.sentence_ending_masuyoune_enabled,
        'prose_style.sentence_endings.variants.masuyoune',
      );
      applyEndingBoolean(
        values.sentence_ending_desuyoune_enabled,
        'prose_style.sentence_endings.variants.desuyoune',
      );
      applyEndingBoolean(
        values.sentence_ending_to_omoimasu_enabled,
        'prose_style.sentence_endings.variants.to_omoimasu',
      );
      applyEndingBoolean(
        values.sentence_ending_kudasai_enabled,
        'prose_style.sentence_endings.variants.kudasai',
      );
      applyEndingBoolean(
        values.sentence_ending_shimashou_enabled,
        'prose_style.sentence_endings.variants.shimashou',
      );

      // 旧形式データのクリーンアップ（新形式に移行済みの場合は削除）
      // variant_mix_per_100_sentences, ratios_per_100_sentences, encouraging を削除
      const cleanupOldFormat = (obj, path) => {
        const parts = path.split('.');
        let current = obj;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) return;
          current = current[parts[i]];
        }
        if (current && parts[parts.length - 1] in current) {
          delete current[parts[parts.length - 1]];
        }
      };

      // variants配下の旧形式を削除
      cleanupOldFormat(
        updated,
        'prose_style.sentence_endings.variants.variant_mix_per_100_sentences',
      );
      cleanupOldFormat(
        updated,
        'prose_style.sentence_endings.variants.run_constraints',
      );
      // run_constraintsは新形式でもvariantsの外に配置する必要がある
      if (updated.prose_style?.sentence_endings?.variants) {
        const runConstraints =
          updated.prose_style.sentence_endings.variants.run_constraints;
        if (runConstraints) {
          updated = setNested(
            updated,
            'prose_style.sentence_endings.run_constraints',
            runConstraints,
          );
        }
        delete updated.prose_style.sentence_endings.variants.run_constraints;
      }
      // 旧形式の ratios_per_100_sentences と encouraging を削除
      cleanupOldFormat(
        updated,
        'prose_style.sentence_endings.ratios_per_100_sentences',
      );
      cleanupOldFormat(updated, 'prose_style.sentence_endings.encouraging');

      // transitionsのpreferredも削除（新形式はconnective_wordsのみ）
      cleanupOldFormat(updated, 'prose_style.transitions.preferred');

      // トップレベルの重複データも削除（tone, rhythm, constraintsはprose_style配下に統一）
      cleanupOldFormat(updated, 'tone');
      cleanupOldFormat(updated, 'rhythm');
      cleanupOldFormat(updated, 'constraints');

      // 中立性/専門性は正式項目として保持（0-100）
      if (typeof values.neutrality === 'number') {
        updated = setNested(
          updated,
          'prose_style.tone.neutrality',
          values.neutrality,
        );
      }
      if (typeof values.expertise === 'number') {
        updated = setNested(
          updated,
          'prose_style.tone.expertise',
          values.expertise,
        );
      }

      // カスタムルール（自由記述）
      // 追加指示（構成案）
      if (typeof values.additional_instructions_outline === 'string') {
        const instructions = values.additional_instructions_outline.trim();
        updated = setNested(
          updated,
          'prose_style.constraints.additional_instructions_outline',
          instructions,
        );
      }
      // 追加指示（本文）
      if (typeof values.additional_instructions_content === 'string') {
        const instructions = values.additional_instructions_content.trim();
        updated = setNested(
          updated,
          'prose_style.constraints.additional_instructions_content',
          instructions,
        );
      }
      // 禁止表現
      if (typeof values.avoid_text === 'string') {
        const lines = values.avoid_text
          .split('\n')
          .map(l => l.trim())
          .filter(Boolean);
        updated = setNested(
          updated,
          'prose_style.constraints.avoid_patterns',
          lines,
        );
      }
      // 置換ルール
      if (typeof values.rewrite_text === 'string') {
        const pairs = values.rewrite_text
          .split('\n')
          .map(l => l.trim())
          .filter(Boolean);
        // 形式: A=>B で保存
        const rules = pairs
          .map(p => {
            const sp = p.split('=>');
            return { from: (sp[0] || '').trim(), to: (sp[1] || '').trim() };
          })
          .filter(r => r.from && r.to);
        if (rules.length > 0) {
          updated = setNested(
            updated,
            'prose_style.constraints.rewrite_rules',
            rules,
          );
        }
      }

      setYamlLoading(true);
      console.log('[YAML Preview] begin convertYaml');
      const res = await v2StylesApi.convertYaml(updated);
      console.log('[YAML Preview] result', res);
      if (!res || typeof res.yaml !== 'string') {
        // デバッグ出力
        console.warn('[YAML Preview] convertYaml no yaml returned', res);
        console.log('[YAML Preview] updated payload', updated);
      }
      // 追加デバッグ: 入力値と更新後ペイロード（単一オブジェクトで出力）
      try {
        const fullValues = configForm.getFieldsValue(true);
        console.log({
          tag: '[Style Config Debug] refreshYamlPreview',
          form_values: fullValues,
          updated_style_config: updated,
        });
      } catch (_) {
        // no-op
      }
      setYamlPreview(res?.yaml || '');
      try {
        setDraftConfig(updated);
      } catch (_) {
        // no-op
      }
    } catch (e) {
      console.error('[YAML Preview] failed', e);
    } finally {
      setYamlLoading(false);
    }
  };

  // 現在のフォーム値から保存用style_configを構築（YAML変換なし）
  const buildUpdatedConfigFromForm = () => {
    const values = configForm.getFieldsValue();
    const base = draftConfig || currentStyle?.style_config || {};
    let updated = { ...base };

    // 基本
    updated = setNested(updated, 'metadata.language', values.language);
    if (values.description !== undefined) {
      updated = setNested(updated, 'metadata.description', values.description);
    }
    if (values.name !== undefined) {
      updated = setNested(updated, 'metadata.title', values.name);
    }

    // 見出し（v2）
    updated = setNested(
      updated,
      'headings_style.allowed_levels',
      values.allowed_levels || [],
    );
    if (typeof values.depth_min === 'number') {
      updated = setNested(
        updated,
        'headings_style.depth.min',
        values.depth_min,
      );
    }
    if (typeof values.depth_max === 'number') {
      updated = setNested(
        updated,
        'headings_style.depth.max',
        values.depth_max,
      );
    }
    if (values.h2_typical) {
      updated = setNested(
        updated,
        'headings_style.counts.h2_per_article.typical',
        values.h2_typical,
      );
    }
    if (values.h3_typical) {
      updated = setNested(
        updated,
        'headings_style.counts.h3_per_h2.typical',
        values.h3_typical,
      );
    }
    if (typeof values.ui_allow_interrogative !== 'undefined') {
      updated = setNested(
        updated,
        'headings_style.temperature.allow_interrogative',
        !!values.ui_allow_interrogative,
      );
    }
    if (Array.isArray(values.ui_heading_symbols)) {
      const sym = {
        question: values.ui_heading_symbols.includes('question'),
        brackets: values.ui_heading_symbols.includes('brackets'),
        colon: values.ui_heading_symbols.includes('colon'),
      };
      updated = setNested(
        updated,
        'headings_style.temperature.allow_symbols',
        sym,
      );
    }
    if (values.h2_chars_typical) {
      updated = setNested(
        updated,
        'headings_style.length_policy.h2_chars.typical',
        values.h2_chars_typical,
      );
    }
    if (values.h3_chars_typical) {
      updated = setNested(
        updated,
        'headings_style.length_policy.h3_chars.typical',
        values.h3_chars_typical,
      );
    }
    if (values.h2_body_chars_typical) {
      updated = setNested(
        updated,
        'headings_style.length_policy.h2_body_chars.typical',
        values.h2_body_chars_typical,
      );
    }
    if (values.h3_body_chars_typical) {
      updated = setNested(
        updated,
        'headings_style.length_policy.h3_body_chars.typical',
        values.h3_body_chars_typical,
      );
    }

    // つなぎ言葉
    if (typeof values.trans_freq !== 'undefined') {
      const transFreq = values.trans_freq; // 既に範囲に変換済み（必要に応じて再利用）
      updated = setNested(
        updated,
        'prose_style.transitions.frequency_per_1000w.typical',
        transFreq,
      );
    }
    // つなぎ言葉（connective_words: 3段階管理）
    if (
      values.connective_words_data &&
      typeof values.connective_words_data === 'object'
    ) {
      updated = setNested(
        updated,
        'prose_style.transitions.connective_words',
        values.connective_words_data,
      );
    }

    // フォーマット
    if (typeof values.code_inline_allowed !== 'undefined') {
      updated = setNested(
        updated,
        'prose_style.formatting.emphasis.code_inline_allowed',
        !!values.code_inline_allowed,
      );
    }
    if (typeof values.bullets_preset !== 'undefined') {
      const bulletsFreq = bulletsPresetToRange(values.bullets_preset);
      updated = setNested(
        updated,
        'prose_style.formatting.bullets.frequency_per_1000w.typical',
        bulletsFreq,
      );
    }
    if (typeof values.tables_preset !== 'undefined') {
      const tablesFreq = tablesPresetToRange(values.tables_preset);
      updated = setNested(
        updated,
        'prose_style.formatting.tables.frequency_per_1000w.typical',
        tablesFreq,
      );
    }
    // 作法
    const bulletRules = {
      max_nesting:
        typeof values.bullets_max_nesting === 'number'
          ? values.bullets_max_nesting
          : 1,
      item_max_chars:
        typeof values.bullets_item_max_chars === 'number'
          ? values.bullets_item_max_chars
          : 80,
      terminal_punct_disallowed: !!values.bullets_terminal_punct_disallowed,
      parallelism_required: !!values.bullets_parallelism_required,
    };
    updated = setNested(
      updated,
      'prose_style.formatting.rules.bullets',
      bulletRules,
    );
    const tableRules = {
      usage: Array.isArray(values.tables_usage)
        ? values.tables_usage
        : ['比較', '要点整理'],
      header_required: !!values.tables_header_required,
      caption_allowed: !!values.tables_caption_allowed,
    };
    updated = setNested(
      updated,
      'prose_style.formatting.rules.tables',
      tableRules,
    );

    // 文体・リズム
    if (typeof values.politeness === 'number') {
      const p = values.politeness;
      const politenessStr = p <= 33 ? '常体' : p >= 67 ? 'です/ます' : '混合';
      const formality = p <= 33 ? 'low' : p >= 67 ? 'high' : 'medium';
      updated = setNested(
        updated,
        'prose_style.tone.register.politeness',
        politenessStr,
      );
      updated = setNested(
        updated,
        'prose_style.tone.register.formality',
        formality,
      );
      if (p >= 67) {
        updated = setNested(
          updated,
          'prose_style.sentence_endings.ratios_per_100_sentences.desu_masu',
          '85-100',
        );
        updated = setNested(
          updated,
          'prose_style.sentence_endings.ratios_per_100_sentences.da_dearu',
          '0-10',
        );
      } else if (p <= 33) {
        const desuMasuMin = p <= 5 ? '0-0' : '0-30';
        updated = setNested(
          updated,
          'prose_style.sentence_endings.ratios_per_100_sentences.desu_masu',
          desuMasuMin,
        );
        updated = setNested(
          updated,
          'prose_style.sentence_endings.ratios_per_100_sentences.da_dearu',
          p <= 5 ? '90-100' : '60-100',
        );
      } else {
        updated = setNested(
          updated,
          'prose_style.sentence_endings.ratios_per_100_sentences.desu_masu',
          '30-70',
        );
        updated = setNested(
          updated,
          'prose_style.sentence_endings.ratios_per_100_sentences.da_dearu',
          '10-30',
        );
      }
    }
    if (typeof values.sentence_length === 'number') {
      const avg =
        values.sentence_length <= 33
          ? '25-40'
          : values.sentence_length >= 66
            ? '50-70'
            : '35-55';
      updated = setNested(
        updated,
        'prose_style.rhythm.sentence_length_chars.avg',
        avg,
      );
    }
    if (typeof values.energy === 'number') {
      updated = setNested(updated, 'prose_style.tone.intensity', values.energy);
    }
    if (typeof values.info_density === 'number') {
      updated = setNested(
        updated,
        'prose_style.rhythm.info_density',
        values.info_density,
      );
    }
    if (typeof values.neutrality === 'number') {
      updated = setNested(
        updated,
        'prose_style.tone.neutrality',
        values.neutrality,
      );
    }
    if (typeof values.expertise === 'number') {
      updated = setNested(
        updated,
        'prose_style.tone.expertise',
        values.expertise,
      );
    }

    // 調査フィルター（domain_filter）
    const domainFilterMode = values.domain_filter_mode || 'disabled';
    const domainFilter = {
      mode: domainFilterMode,
      whitelist: [],
      blacklist: [],
    };
    
    // ホワイトリスト
    if (values.domain_filter_whitelist && typeof values.domain_filter_whitelist === 'string') {
      domainFilter.whitelist = values.domain_filter_whitelist
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .slice(0, 20);  // 最大20件
    }
    
    // ブラックリスト
    if (values.domain_filter_blacklist && typeof values.domain_filter_blacklist === 'string') {
      domainFilter.blacklist = values.domain_filter_blacklist
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .slice(0, 20);  // 最大20件
    }
    
    updated = setNested(updated, 'domain_filter', domainFilter);

    // カスタムルール（constraints: refreshYamlPreviewと同じ変換ロジック）
    // 追加指示（構成案）
    if (typeof values.additional_instructions_outline === 'string') {
      const instructions = values.additional_instructions_outline.trim();
      updated = setNested(
        updated,
        'prose_style.constraints.additional_instructions_outline',
        instructions || '',
      );
    }
    // 追加指示（本文）
    if (typeof values.additional_instructions_content === 'string') {
      const instructions = values.additional_instructions_content.trim();
      updated = setNested(
        updated,
        'prose_style.constraints.additional_instructions_content',
        instructions || '',
      );
    }
    // 禁止表現
    if (typeof values.avoid_text === 'string') {
      const lines = values.avoid_text
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);
      updated = setNested(
        updated,
        'prose_style.constraints.avoid_patterns',
        lines,
      );
    }
    // 置換ルール
    if (typeof values.rewrite_text === 'string') {
      const pairs = values.rewrite_text
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);
      const rules = pairs
        .map(p => {
          const sp = p.split('=>');
          return { from: (sp[0] || '').trim(), to: (sp[1] || '').trim() };
        })
        .filter(r => r.from && r.to);
      updated = setNested(
        updated,
        'prose_style.constraints.rewrite_rules',
        rules,
      );
    }

    return updated;
  };

  // ユーザー権限
  const [userProfile, setUserProfile] = useState(null);
  const isAdmin =
    userProfile?.role === 'admin' || userProfile?.role === 'master';

  useEffect(() => {
    (async () => {
      try {
        const prof = await getUserProfile();
        setUserProfile(prof);
      } catch (_) {
        setUserProfile(null);
      }
    })();
  }, []);

  // 管理者表示用: 作成者情報のフォールバックを付与（APIが返さない場合は自分のプロフィールで補完）
  useEffect(() => {
    if (!isAdmin || !userProfile) return;
    setStyles(prev =>
      (prev || []).map(s => ({
        ...s,
        company_name:
          s.company_name ||
          userProfile.company_name ||
          userProfile.company ||
          null,
        user_name:
          s.user_name ||
          userProfile.user_name ||
          userProfile.name ||
          [userProfile.last_name, userProfile.first_name]
            .filter(Boolean)
            .join('') ||
          null,
        user_email: s.user_email || userProfile.email || null,
      })),
    );
  }, [isAdmin, userProfile]);

  // ユーザー情報の取得
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const profile = await getUserProfile();
        setUserRole(profile.role);

        // 管理者の場合、ユーザー一覧を取得
        if (profile.role === 'admin' || profile.role === 'master') {
          const response = await api.get('/users/', {
            params: { page_size: 1000 },
          });
          setUsers(response.data?.users || []);
        }
      } catch (e) {
        console.error('Failed to get user profile:', e);
      }
    };

    // 機能制御状態を取得
    const fetchFeatureStatus = async () => {
      try {
        const response = await gamificationApi.getFeaturesStatus();
        const styleStatus = response.features?.custom_style || {};
        const slotsStatus = response.features?.custom_style_slots || {};
        const planDisplayNames = { lite: 'ライト', standard: 'スタンダード', premium: 'プレミアム' };
        setFeatureStatus({
          unlocked: styleStatus.unlocked || false,
          currentCount: slotsStatus.current_count ?? styleStatus.current_count ?? 0,
          maxLimit: slotsStatus.max_limit ?? 0,
          requiredPlan: planDisplayNames[styleStatus.required_plan] || styleStatus.required_plan || '',
        });
      } catch (e) {
        console.error('Failed to get feature status:', e);
      } finally {
        setFeatureLoading(false);
      }
    };

    fetchUserInfo();
    fetchFeatureStatus();
  }, []);

  // 一覧取得
  const loadStyles = async () => {
    try {
      setLoading(true);
      const params = {};
      // 管理者の場合、選択されたユーザーIDをパラメータに追加
      // デフォルトスタイルは常に表示（フロントエンドでマージ）
      if ((userRole === 'admin' || userRole === 'master') && selectedUserId) {
        params.user_id = selectedUserId;
      }
      console.log(
        '[StyleManagement] Fetching with params:',
        params,
        'userRole:',
        userRole,
        'selectedUserId:',
        selectedUserId,
      );
      const data = await v2StylesApi.getStyles(params);
      console.log('[StyleManagement] Response count:', data?.length);
      console.log('[StyleManagement] API response:', data);
      const mapped = (data || []).map(s => ({
        id: s.id,
        name: s.name,
        description: s.description || '',
        category: 'custom',
        sourceUrl: s.source_url || s.sourceUrl,
        createdAt: s.created_at || s.createdAt,
        updatedAt: s.updated_at || s.updatedAt,
        usage: s.usage_count || 0,
        lastUsed: s.last_used_at || null,
        settings: {},
        style_config: s.style_config,
        // デフォルトスタイル管理フィールド
        is_default: s.is_default || false,
        is_editable: s.is_editable !== undefined ? s.is_editable : true,
        display_order: s.display_order || 0,
        // 作成者情報（生成結果一覧ツールチップと同等のキー名）
        company_name: s.company_name || s.company || s.owner_company || null,
        user_name: s.user_name || s.owner_name || s.created_by_name || null,
        user_email: s.user_email || s.owner_email || s.created_by_email || null,
      }));
      console.log('[StyleManagement] Mapped styles:', mapped);
      setStyles(mapped);
    } catch (e) {
      message.error('スタイル一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole) {
      loadStyles();
    }
  }, [userRole, selectedUserId]);

  // 設定モーダルが開いたら確実にYAMLプレビューを更新
  useEffect(() => {
    if (configModalVisible && currentStyle) {
      const t = setTimeout(() => {
        try {
          console.debug('[YAML Preview] effect trigger');
          refreshYamlPreview();
        } catch (e) {
          console.error('[YAML Preview] effect failed', e);
        }
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [configModalVisible, currentStyle]);

  // URL解析処理（実API）
  const analyzeUrl = async url => {
    setAnalyzingUrl(true);
    try {
      const created = await v2StylesApi.analyzeStyle({ source_url: url });
      // 一覧を更新
      await loadStyles();
      // プレビューを表示
      const previewItem = {
        id: created.id,
        name: created.name,
        description: created.description || '',
        category: 'custom',
        sourceUrl: created.source_url,
        createdAt: created.created_at,
        updatedAt: created.updated_at,
        usage: created.usage_count || 0,
        lastUsed: created.last_used_at || null,
        settings: {},
        style_config: created.style_config,
      };
      setCurrentStyle(previewItem);
      setPreviewModalVisible(true);
      message.success('スタイルを解析・保存しました');
      // モーダルは閉じる
      setCreateModalVisible(false);
      setCurrentStep(0);
      setAnalysisResult(null);
      createForm.resetFields();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'URL解析に失敗しました');
    } finally {
      setAnalyzingUrl(false);
    }
  };

  const analyzeText = async text => {
    setAnalyzingUrl(true);
    try {
      const created = await v2StylesApi.analyzeStyle({ article_text: text });
      // 一覧を更新
      await loadStyles();
      // プレビューを表示
      const previewItem = {
        id: created.id,
        name: created.name,
        description: created.description || '',
        category: 'custom',
        createdAt: created.created_at,
        updatedAt: created.updated_at,
        usage: created.usage_count || 0,
        lastUsed: created.last_used_at || null,
        settings: {},
        style_config: created.style_config,
      };
      setCurrentStyle(previewItem);
      setPreviewModalVisible(true);
      message.success('スタイルを解析・保存しました');
      // モーダルは閉じる
      setCreateModalVisible(false);
      setCurrentStep(0);
      setAnalysisResult(null);
      createForm.resetFields();
    } catch (error) {
      message.error(error?.response?.data?.detail || '原稿解析に失敗しました');
    } finally {
      setAnalyzingUrl(false);
    }
  };

  // YAML生成
  const generateYaml = settings => {
    const yaml = `# スタイル設定
name: ${settings.name || 'カスタムスタイル'}
description: ${settings.description || ''}
category: ${settings.category || 'custom'}
source_url: ${settings.sourceUrl || ''}

# トーン・文体設定
tone:
  formality: ${settings.settings.tone.formality}  # フォーマル度 (0-100)
  friendliness: ${settings.settings.tone.friendliness}  # 親しみやすさ (0-100)
  expertise: ${settings.settings.tone.expertise}  # 専門性 (0-100)
  enthusiasm: ${settings.settings.tone.enthusiasm}  # 熱量 (0-100)
  sentence_length: ${settings.settings.tone.sentence_length}  # センテンスの長さ
  vocabulary: ${settings.settings.tone.vocabulary}  # 語彙レベル

# 筆者の立ち位置
position:
  perspective: ${settings.settings.position.perspective}  # 視点
  authority: ${settings.settings.position.authority}  # 権威性の演出 (0-100)
  objectivity: ${settings.settings.position.objectivity}  # 客観性 (0-100)
  personality: ${settings.settings.position.personality}  # 個性の表出 (0-100)

# 構造・見出し
structure:
  heading_style: ${settings.settings.structure.heading_style}  # 見出しスタイル
  heading_length: ${settings.settings.structure.heading_length}  # 見出しの長さ
  section_depth: ${settings.settings.structure.section_depth}  # セクション階層
  intro_style: ${settings.settings.structure.intro_style}  # 導入部スタイル
  conclusion_style: ${settings.settings.structure.conclusion_style}  # まとめスタイル

# サービス訴求
promotion:
  promotion_frequency: ${settings.settings.promotion.promotion_frequency}  # 訴求頻度 (0-100)
  promotion_style: ${settings.settings.promotion.promotion_style}  # 訴求スタイル
  cta_position: ${settings.settings.promotion.cta_position}  # CTA配置
  urgency: ${settings.settings.promotion.urgency}  # 緊急性の演出 (0-100)

# エンゲージメント
engagement:
  question_frequency: ${settings.settings.engagement.question_frequency}  # 問いかけ頻度 (0-100)
  example_usage: ${settings.settings.engagement.example_usage}  # 例示の使用 (0-100)
  data_presentation: ${settings.settings.engagement.data_presentation}  # データ提示 (0-100)
  visual_elements: ${settings.settings.engagement.visual_elements}  # 視覚要素の活用 (0-100)
`;
    return yaml;
  };

  // フィルタリング
  const filteredStyles = styles.filter(style => {
    return (
      style.name.toLowerCase().includes(searchText.toLowerCase()) ||
      style.description.toLowerCase().includes(searchText.toLowerCase())
    );
  });

  // 削除処理
  const handleDelete = id => {
    confirm({
      title: '削除確認',
      content: 'このスタイルを削除してもよろしいですか？',
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      async onOk() {
        try {
          await v2StylesApi.deleteStyle(id);
          setStyles(prev => prev.filter(s => s.id !== id));
          message.success('削除しました');
        } catch (e) {
          message.error(e?.response?.data?.detail || '削除に失敗しました');
        }
      },
    });
  };

  // テーブルカラム定義（生成結果一覧の仕様に合わせる）
  const columns = (() => {
    const base = [
      {
        title: 'スタイル名',
        dataIndex: 'name',
        key: 'name',
        width: 200,
        render: (text, record) => (
          <span
            onClick={() => openConfigForStyle(record)}
            style={{ color: '#000', fontWeight: 600 }}
          >
            {text}
          </span>
        ),
      },
      {
        title: 'メモ',
        dataIndex: 'description',
        key: 'description',
        width: 200,
      },
      {
        title: '更新日',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 120,
        render: date => moment(date).format('YYYY/MM/DD'),
        sorter: (a, b) =>
          moment(a.updatedAt).unix() - moment(b.updatedAt).unix(),
      },
    ];

    if (isAdmin) {
      const ownerCol = {
        title: '作成者',
        key: 'owner',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <Tooltip
            title={
              <span style={{ whiteSpace: 'pre-line' }}>
                {[record.company_name, record.user_name, record.user_email]
                  .filter(Boolean)
                  .join('\n')}
              </span>
            }
          >
            <span
              onClick={e => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#f0f0f0',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                color: '#595959',
              }}
            >
              {(record.user_name || record.user_email || 'U')
                .toString()
                .trim()
                .slice(0, 1)
                .toUpperCase()}
            </span>
          </Tooltip>
        ),
      };
      // 更新日の右に作成者列を挿入
      base.splice(3, 0, ownerCol);
    }

    // アクション列（削除のみ）。行クリック対象外にするためイベント伝播を止める
    base.push({
      title: '',
      key: 'action',
      width: 64,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        // デフォルトスタイルの場合は削除ボタンを非表示
        if (record.is_default) {
          return (
            <Tooltip title="デフォルトスタイルは削除できません">
              <DeleteOutlined
                style={{ color: '#d9d9d9', cursor: 'not-allowed' }}
              />
            </Tooltip>
          );
        }

        return (
          <Popconfirm
            title="スタイルの削除"
            description={`「${record.name}」を削除します。よろしいですか？`}
            okText="削除"
            cancelText="キャンセル"
            okButtonProps={{ danger: true }}
            onConfirm={e => {
              // 確認操作も行クリックさせない
              e?.stopPropagation?.();
              handleDelete(record.id);
            }}
            onCancel={e => e?.stopPropagation?.()}
          >
            <Tooltip title="削除">
              <DeleteOutlined
                onClick={e => e.stopPropagation()}
                style={{ color: '#ff4d4f', cursor: 'pointer' }}
              />
            </Tooltip>
          </Popconfirm>
        );
      },
    });

    return base;
  })();

  // カードビューのレンダリング
  const renderCardView = () => {
    // スタイル設定から情報を抽出するヘルパー関数
    const getStyleMetrics = style => {
      const cfg = style.style_config || {};
      console.log(`[getStyleMetrics] ${style.name}:`, {
        cfg,
        headings_style: cfg.headings_style,
        tone: cfg.tone,
      });

      // 想定文字数の計算
      // YAMLの正しい構造: headings_style.counts.h2_per_article.typical
      const h2nStr = cfg.headings_style?.counts?.h2_per_article?.typical || '';
      const h3nStr = cfg.headings_style?.counts?.h3_per_h2?.typical || '';
      const h2bStr =
        cfg.headings_style?.length_policy?.h2_body_chars?.typical || '';
      const h3bStr =
        cfg.headings_style?.length_policy?.h3_body_chars?.typical || '';
      console.log(`[getStyleMetrics] ${style.name} heading data:`, {
        h2nStr,
        h3nStr,
        h2bStr,
        h3bStr,
      });

      const parseRangeValues = s => {
        if (!s || typeof s !== 'string') return [0, 0, 0];
        const m = s.split('-').map(x => Number(String(x).trim()));
        if (m.length >= 2) {
          const a = Number.isFinite(m[0]) ? m[0] : 0;
          const b = Number.isFinite(m[1]) ? m[1] : a;
          const lo = Math.min(a, b);
          const hi = Math.max(a, b);
          const mid = (lo + hi) / 2;
          return [lo, hi, mid];
        }
        const v = Number.isFinite(m[0]) ? m[0] : 0;
        return [v, v, v];
      };

      const [h2Min, h2Max] = parseRangeValues(h2nStr);
      const [h3Min, h3Max, h3Mid] = parseRangeValues(h3nStr);
      const [h2BodyMin, h2BodyMax] = parseRangeValues(h2bStr);
      const [h3BodyMin, h3BodyMax, h3BodyMid] = parseRangeValues(h3bStr);

      let totalChars = '—';
      if (h2Max && h2BodyMax) {
        const ratioMaxUse = 0.8;
        const estMinBase = h2Min * (h2BodyMin + 1 * h3Mid * h3BodyMid);
        const estMaxBase =
          h2Max * (h2BodyMax + ratioMaxUse * h3Max * h3BodyMax);
        const minEst = Math.max(0, Math.round(estMinBase / 100) * 100);
        const maxEst = Math.max(minEst, Math.round(estMaxBase / 100) * 100);
        totalChars = `${minEst.toLocaleString()}-${maxEst.toLocaleString()}`;
      }

      // スライダー値の取得（スタイルプレビューと同じ計算）
      // 1. 文の長さ: sentence_length_chars.avg から0-100のパーセンテージへ
      const parseAvg = s => {
        if (!s || typeof s !== 'string') return 0;
        const parts = s.split('-').map(x => Number(String(x).trim()));
        if (parts.length === 1 && !Number.isNaN(parts[0])) return parts[0];
        if (parts.length >= 2) {
          const a = Number.isNaN(parts[0]) ? 0 : parts[0];
          const b = Number.isNaN(parts[1]) ? a : parts[1];
          return (a + b) / 2;
        }
        return 0;
      };
      const sentenceLenAvg = parseAvg(
        cfg.prose_style?.rhythm?.sentence_length_chars?.avg || '',
      );
      const sentenceLength = Math.max(
        0,
        Math.min(100, Math.round((sentenceLenAvg / 80) * 100)),
      );

      // 2. トーン: intensity
      const toneIntensity = cfg.prose_style?.tone?.intensity || 50;

      // 3. 語彙性: info_density
      const vocabDensity = cfg.prose_style?.rhythm?.info_density || 50;

      // 4. 専門性: expertise
      const expertise = cfg.prose_style?.tone?.expertise || 50;

      return {
        totalChars,
        h2Count: h2nStr || '—',
        h3Count: h3nStr || '—',
        sentenceLength,
        toneIntensity,
        vocabDensity,
        expertise,
      };
    };

    return (
      <Row gutter={[16, 16]}>
        {filteredStyles.map(style => {
          const metrics = getStyleMetrics(style);
          const actions = [];

          // 編集アイコン（常に表示）
          actions.push(
            <Tooltip key="settings" title="編集">
              <SettingOutlined
                onClick={e => {
                  e.stopPropagation();
                  openConfigForStyle(style);
                }}
              />
            </Tooltip>,
          );

          // 削除アイコン（デフォルトスタイルの場合は非表示）
          console.log(
            `[StyleCard] ${style.name}: is_default=${style.is_default}, type=${typeof style.is_default}`,
          );
          if (!style.is_default) {
            actions.push(
              <Tooltip key="delete" title="削除">
                <DeleteOutlined
                  onClick={e => {
                    e.stopPropagation();
                    handleDelete(style.id);
                  }}
                  style={{ color: '#ff4d4f' }}
                />
              </Tooltip>,
            );
          }

          return (
            <Col key={style.id} xs={24} sm={12} md={8}>
              <Card
                hoverable
                onClick={() => openConfigForStyle(style)}
                style={{ cursor: 'pointer' }}
                actions={actions}
              >
                <Card.Meta
                  title={
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      {style.name}
                      {style.is_default && (
                        <Tag color="blue" style={{ margin: 0 }}>
                          デフォルト
                        </Tag>
                      )}
                    </div>
                  }
                  description={
                    <div>
                      <Paragraph
                        type="secondary"
                        ellipsis={{ rows: 2 }}
                        style={{
                          marginBottom: 10,
                          fontSize: 12,
                        }}
                      >
                        {style.description}
                      </Paragraph>

                      {/* スタイルメトリクス - プレビュー形式 */}
                      <div style={{ marginTop: 12 }}>
                        {/* 想定文字数とH2/H3個数 */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '6fr 4fr',
                            gap: 12,
                            marginBottom: 12,
                          }}
                        >
                          <div className="style-kpi">
                            <div className="label">想定文字数</div>
                            <div className="value" style={{ fontSize: 18 }}>
                              {metrics.totalChars}
                              <span className="unit">文字</span>
                            </div>
                          </div>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: 8,
                            }}
                          >
                            <div className="style-kpi">
                              <div className="label" style={{ fontSize: 11 }}>
                                H2
                              </div>
                              <div className="value" style={{ fontSize: 16 }}>
                                {metrics.h2Count}
                                <span className="unit">個</span>
                              </div>
                            </div>
                            <div className="style-kpi">
                              <div className="label" style={{ fontSize: 11 }}>
                                H3
                              </div>
                              <div className="value" style={{ fontSize: 16 }}>
                                {metrics.h3Count}
                                <span className="unit">個</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* スライダー表示 */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 8,
                            marginTop: 12,
                          }}
                        >
                          {[
                            {
                              label: '文の長さ',
                              value: metrics.sentenceLength,
                            },
                            { label: 'トーン', value: metrics.toneIntensity },
                            { label: '語彙性', value: metrics.vocabDensity },
                            { label: '専門性', value: metrics.expertise },
                          ].map((item, idx) => (
                            <div
                              key={idx}
                              className="barcard card-slider-mini"
                              style={{ padding: '6px 2px' }}
                            >
                              <div
                                className="barlabel"
                                style={{ fontSize: 10, marginBottom: 4 }}
                              >
                                {item.label}
                              </div>
                              <Slider
                                value={item.value}
                                disabled
                                className="viz-slider viz-slider-mini"
                                tooltip={{ open: false }}
                                style={{
                                  margin: '4px 0 0',
                                  pointerEvents: 'none',
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  }
                />
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  // 設定項目のレンダリング
  const renderSettingItem = (
    category,
    itemKey,
    item,
    value,
    onChange,
    disabled = false,
  ) => {
    switch (item.type) {
      case 'slider':
        return (
          <div key={itemKey} style={{ marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <Text>{item.label}</Text>
              <InputNumber
                min={item.min}
                max={item.max}
                value={value}
                onChange={onChange}
                size="small"
                disabled={disabled}
              />
            </div>
            <Slider
              min={item.min}
              max={item.max}
              value={value}
              onChange={onChange}
              disabled={disabled}
            />
          </div>
        );
      case 'select':
        return (
          <Form.Item
            key={itemKey}
            label={item.label}
            style={{ marginBottom: 16 }}
          >
            <Select value={value} onChange={onChange} disabled={disabled}>
              {item.options.map(option => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );
      case 'number':
        return (
          <Form.Item
            key={itemKey}
            label={item.label}
            style={{ marginBottom: 16 }}
          >
            <InputNumber
              min={item.min}
              max={item.max}
              value={value}
              onChange={onChange}
              disabled={disabled}
            />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  const bulletsPresetToRange = preset => {
    switch (preset) {
      case 'very_low':
        return '0-1';
      case 'low':
        return '1-2';
      case 'slightly_low':
        return '2-3';
      case 'standard':
        return '2-4';
      case 'slightly_high':
        return '4-6';
      case 'high':
        return '5-8';
      case 'very_high':
        return '6-8';
      default:
        return '2-4';
    }
  };
  const tablesPresetToRange = preset => {
    switch (preset) {
      case 'very_low':
        return '0-1';
      case 'low':
        return '1-2';
      case 'slightly_low':
        return '1-2';
      case 'standard':
        return '2-3';
      case 'slightly_high':
        return '3-4';
      case 'high':
        return '4-6';
      case 'very_high':
        return '5-7';
      default:
        return '2-3';
    }
  };

  // ツアーステップを生成（target関数はEnhancedTour内で動的に評価される）
  const tourSteps = React.useMemo(() => {
    if (tourLoading || !TOUR_CONFIGS.style_management) return [];
    return TOUR_CONFIGS.style_management.steps;
  }, [tourLoading]);

  // ツアーステップ変更時のアクション処理
  const handleTourStepChange = (newStep) => {
    const prevStepConfig = TOUR_CONFIGS.style_management?.steps[tourCurrentStep];
    
    // 前のステップにopen_create_modalアクションがある場合、作成モーダルを開く
    if (prevStepConfig?.action === 'open_create_modal' && newStep > tourCurrentStep) {
      setCreateModalVisible(true);
      setTimeout(() => {
        updateTourCurrentStep(newStep);
      }, 400);
      return;
    }
    
    // 前のステップにclose_create_modalアクションがある場合、作成モーダルを閉じる
    if (prevStepConfig?.action === 'close_create_modal' && newStep > tourCurrentStep) {
      setCreateModalVisible(false);
      setTimeout(() => {
        updateTourCurrentStep(newStep);
      }, 400);
      return;
    }
    
    // 前のステップにopen_edit_modalアクションがある場合、編集モーダルを開く
    if (prevStepConfig?.action === 'open_edit_modal' && newStep > tourCurrentStep) {
      // 最初のスタイルを選択して編集モーダルを開く
      const firstStyle = styles[0];
      if (firstStyle) {
        openConfigForStyle(firstStyle);
      }
      setTimeout(() => {
        updateTourCurrentStep(newStep);
      }, 400);
      return;
    }
    
    // 前のステップにclose_edit_modalアクションがある場合、編集モーダルを閉じる
    if (prevStepConfig?.action === 'close_edit_modal' && newStep > tourCurrentStep) {
      setConfigModalVisible(false);
      setTimeout(() => {
        updateTourCurrentStep(newStep);
      }, 400);
      return;
    }
    
    // 通常のステップ変更
    updateTourCurrentStep(newStep);
  };

  return (
    <>
      {/* ツアー */}
      {tourSteps.length > 0 && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          onClose={() => {
            setCreateModalVisible(false);
            setConfigModalVisible(false);
            completeTour();
          }}
          current={tourCurrentStep}
          onChange={handleTourStepChange}
          onSkip={() => {
            setCreateModalVisible(false);
            setConfigModalVisible(false);
            skipTour();
          }}
          allowSkip={true}
          tourTitle="スタイル管理ガイド"
          mask={true}
        />
      )}

      <Helmet>
        <title>スタイル管理 | SEO記事生成 v2 - magicss</title>
        <meta name="robots" content="noindex" />
        <meta
          property="og:title"
          content="スタイル管理 | SEO記事生成 v2 - magicss"
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${process.env.PUBLIC_URL}/img/ogp.png`}
        />
        <meta
          property="og:description"
          content="SEO記事生成v2のスタイル管理ページです。"
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0' }}>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/aio/dashboard">AIOコンテンツモード</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>スタイル管理</Breadcrumb.Item>
        </Breadcrumb>
        {!featureLoading && (featureStatus.unlocked || userRole === 'admin' || userRole === 'master') && (
          <TourHelpButton
            onClick={restartTour}
            tooltip="ガイドツアーを再表示"
          />
        )}
      </div>

      <div className="style-management">
        {/* ヘッダー */}
        <div style={{ marginBottom: 24 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title
                level={1}
                className="gradient-text"
                style={{ fontSize: '24px' }}
              >
                スタイル管理
              </Title>
              <Text type="secondary">
                記事のトーン、文体、構造などのスタイルを管理します
              </Text>
            </Col>
            <Col>{/* ボタンは下のアクションバーに集約 */}</Col>
          </Row>
        </div>

        {/* TIPS（スタイル管理用） */}
        <div style={{ marginBottom: 16 }}>
          <TipsRotator tips={styleTips} intervalMs={7000} fadeMs={300} />
        </div>

        {/* アクションバー（独自情報管理と同等の配置） */}
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Space wrap>
            {/* 機能ロック状態の表示 */}
            {featureLoading ? (
              <Button
                type="primary"
                icon={<LoadingOutlined />}
                disabled
                style={{ minWidth: 160, fontWeight: 'bold' }}
              >
                読み込み中
              </Button>
            ) : !featureStatus.unlocked &&
            userRole !== 'admin' &&
            userRole !== 'master' ? (
              <Tooltip
                title={`カスタムスタイル機能は${featureStatus.requiredPlan}プラン以上で利用可能です`}
              >
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  disabled
                  style={{ minWidth: 160, fontWeight: 'bold' }}
                >
                  ロック中
                </Button>
              </Tooltip>
            ) : featureStatus.currentCount >= featureStatus.maxLimit &&
              featureStatus.maxLimit > 0 &&
              userRole !== 'admin' &&
              userRole !== 'master' ? (
              <Tooltip
                title={`登録上限（${featureStatus.maxLimit}件）に達しました。上位プランで上限が増加します`}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled
                  style={{ minWidth: 160, fontWeight: 'bold' }}
                >
                  上限到達
                </Button>
              </Tooltip>
            ) : (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => (window.location.href = '/aio/style/new')}
                style={{ minWidth: 160, fontWeight: 'bold' }}
              >
                新規作成
              </Button>
            )}
            <Input
              placeholder="スタイル名、メモで検索..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              allowClear
              style={{ minWidth: 240 }}
            />
            {(userRole === 'admin' || userRole === 'master') && (
              <Select
                allowClear
                showSearch
                placeholder="全てのユーザー"
                value={selectedUserId}
                onChange={setSelectedUserId}
                style={{ minWidth: 200 }}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[
                  { value: null, label: '全てのユーザー' },
                  ...users.map(user => ({
                    value: user.user_id,
                    label: `${user.last_name}${user.first_name} (${user.email})${
                      user.company_name ? ` - ${user.company_name}` : ''
                    }`,
                  })),
                ]}
              />
            )}
          </Space>
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              { value: 'table', icon: <BarsOutlined /> },
              { value: 'card', icon: <AppstoreOutlined /> },
            ]}
          />
        </div>

        {/* メインコンテンツ */}
        {viewMode === 'table' ? (
          <div>
            <Table
              columns={columns}
              dataSource={filteredStyles}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1200 }}
              rowClassName={() => 'row-hoverable'}
              onRow={record => ({
                onClick: e => {
                  // 安全のため、アクションセル経由のクリックは無視
                  const target = e.target;
                  if (
                    target.closest &&
                    (target.closest('.ant-popover') ||
                      target.closest('.ant-popconfirm') ||
                      target.closest('.ant-tooltip') ||
                      target.closest('.anticon-delete'))
                  ) {
                    return;
                  }
                  openConfigForStyle(record);
                },
              })}
              pagination={{
                showSizeChanger: true,
                showTotal: total => `全 ${total} 件`,
              }}
            />
          </div>
        ) : (
          renderCardView()
        )}

        {/* 新規作成モーダル */}
        <Modal
          title="新規スタイル作成"
          open={createModalVisible}
          onCancel={() => {
            setCreateModalVisible(false);
            setCurrentStep(0);
            setAnalysisResult(null);
            createForm.resetFields();
          }}
          footer={null}
          width={900}
        >
          <Steps current={currentStep} style={{ marginBottom: 24 }}>
            <Step title="URL解析" icon={<LinkOutlined />} />
            <Step title="設定調整" icon={<SettingOutlined />} />
            <Step title="確認・保存" icon={<SaveOutlined />} />
          </Steps>

          {currentStep === 0 && (
            <div>
              <Alert
                message="お手本となる記事のURLまたは原稿を入力してください"
                description="記事の構造、文体、トーンなどを自動的に解析し、スタイル設定を生成します。"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Segmented
                options={[
                  {
                    label: 'URLから解析',
                    value: 'url',
                    icon: <LinkOutlined />,
                  },
                  {
                    label: '原稿から解析',
                    value: 'text',
                    icon: <FileTextOutlined />,
                  },
                ]}
                value={createForm.getFieldValue('inputMode') || 'url'}
                onChange={value => {
                  createForm.setFieldsValue({ inputMode: value });
                  createForm.resetFields(['analyzeUrl', 'analyzeText']);
                }}
                block
                style={{ marginBottom: 24 }}
              />

              <Form form={createForm} layout="vertical">
                <Form.Item name="inputMode" hidden initialValue="url">
                  <Input />
                </Form.Item>

                {createForm.getFieldValue('inputMode') === 'url' ? (
                  <>
                    <Form.Item
                      name="analyzeUrl"
                      label="記事URL"
                      rules={[
                        { required: true, message: 'URLを入力してください' },
                        { type: 'url', message: '有効なURLを入力してください' },
                      ]}
                    >
                      <Input
                        placeholder="https://example.com/article"
                        prefix={<LinkOutlined />}
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        loading={analyzingUrl}
                        onClick={() => {
                          createForm
                            .validateFields(['analyzeUrl'])
                            .then(values => {
                              analyzeUrl(values.analyzeUrl);
                            });
                        }}
                        block
                        size="large"
                      >
                        URLを解析
                      </Button>
                    </Form.Item>
                  </>
                ) : (
                  <>
                    <Form.Item
                      name="analyzeText"
                      label={
                        <span>
                          原稿テキスト
                          <Text
                            type="secondary"
                            style={{ marginLeft: 8, fontSize: 12 }}
                          >
                            （最大20,000文字）
                          </Text>
                        </span>
                      }
                      rules={[
                        { required: true, message: '原稿を入力してください' },
                        {
                          max: 20000,
                          message: '原稿は20,000文字以内で入力してください',
                        },
                      ]}
                    >
                      <TextArea
                        rows={12}
                        placeholder="解析する記事の原稿を入力してください..."
                        showCount
                        maxLength={20000}
                        style={{ fontFamily: 'monospace' }}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        loading={analyzingUrl}
                        onClick={() => {
                          createForm
                            .validateFields(['analyzeText'])
                            .then(values => {
                              analyzeText(values.analyzeText);
                            });
                        }}
                        block
                        size="large"
                      >
                        原稿を解析
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form>

              {analyzingUrl && (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16 }}>
                    <Text type="secondary">記事を解析中...</Text>
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep === 1 && analysisResult && (
            <div>
              <Alert
                message="解析結果を確認し、必要に応じて調整してください"
                type="success"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Card style={{ marginBottom: 16 }}>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="解析URL" span={2}>
                    <a
                      href={analysisResult.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {analysisResult.url}
                    </a>
                  </Descriptions.Item>
                  <Descriptions.Item label="記事タイトル" span={2}>
                    {analysisResult.title}
                  </Descriptions.Item>
                  <Descriptions.Item label="文字数">
                    {analysisResult.wordCount.toLocaleString()}文字
                  </Descriptions.Item>
                  <Descriptions.Item label="見出し数">
                    {analysisResult.headingCount}個
                  </Descriptions.Item>
                  <Descriptions.Item label="段落数">
                    {analysisResult.paragraphCount}個
                  </Descriptions.Item>
                  <Descriptions.Item label="画像数">
                    {analysisResult.imageCount}枚
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Form form={createForm} layout="vertical">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label="スタイル名"
                      rules={[
                        {
                          required: true,
                          message: 'スタイル名を入力してください',
                        },
                      ]}
                    >
                      <Input placeholder="例: ビジネスブログ標準" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="category"
                      label="カテゴリ"
                      rules={[
                        {
                          required: true,
                          message: 'カテゴリを選択してください',
                        },
                      ]}
                    >
                      <Select placeholder="カテゴリを選択">
                        <Option value="business">Business</Option>
                        <Option value="media">Media</Option>
                        <Option value="blog">Blog</Option>
                        <Option value="custom">Custom</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="description"
                  label="説明"
                  rules={[
                    { required: true, message: '説明を入力してください' },
                  ]}
                >
                  <TextArea
                    rows={2}
                    placeholder="このスタイルの特徴や用途を説明してください"
                  />
                </Form.Item>

                <Collapse defaultActiveKey={['tone']} style={{ marginTop: 24 }}>
                  {Object.entries(styleCategories).map(
                    ([categoryKey, category]) => (
                      <Panel
                        header={
                          <Space>
                            {category.icon}
                            <Text strong>{category.title}</Text>
                          </Space>
                        }
                        key={categoryKey}
                      >
                        {Object.entries(category.items).map(([itemKey, item]) =>
                          renderSettingItem(
                            categoryKey,
                            itemKey,
                            item,
                            analysisResult.detectedSettings[categoryKey]?.[
                              itemKey
                            ],
                            value => {
                              const currentSettings =
                                createForm.getFieldValue('settings') || {};
                              createForm.setFieldsValue({
                                settings: {
                                  ...currentSettings,
                                  [categoryKey]: {
                                    ...currentSettings[categoryKey],
                                    [itemKey]: value,
                                  },
                                },
                              });
                            },
                          ),
                        )}
                      </Panel>
                    ),
                  )}
                </Collapse>

                <div style={{ marginTop: 24, textAlign: 'right' }}>
                  <Space>
                    <Button onClick={() => setCurrentStep(0)}>戻る</Button>
                    <Button type="primary" onClick={() => setCurrentStep(2)}>
                      次へ
                    </Button>
                  </Space>
                </div>
              </Form>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <Alert
                message="設定内容を確認してください"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Card title="プレビュー" style={{ marginBottom: 16 }}>
                <SyntaxHighlighter language="yaml" style={tomorrow}>
                  {generateYaml({
                    ...createForm.getFieldsValue(),
                    settings: analysisResult.detectedSettings,
                  })}
                </SyntaxHighlighter>
              </Card>

              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => setCurrentStep(1)}>戻る</Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => {
                      createForm.validateFields().then(values => {
                        const newStyle = {
                          id: `style-${Date.now()}`,
                          ...values,
                          sourceUrl: analysisResult.url,
                          settings: analysisResult.detectedSettings,
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString(),
                          createdBy: '現在のユーザー',
                          usage: 0,
                          lastUsed: null,
                        };
                        setStyles([newStyle, ...styles]);
                        message.success('スタイルを作成しました');
                        setCreateModalVisible(false);
                        setCurrentStep(0);
                        setAnalysisResult(null);
                        createForm.resetFields();
                      });
                    }}
                  >
                    保存
                  </Button>
                </Space>
              </div>
            </div>
          )}
        </Modal>

        {/* 編集モーダル */}
        <Modal
          title="スタイルを編集"
          open={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            editForm.resetFields();
          }}
          footer={null}
          width={900}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={values => {
              const updatedStyles = styles.map(s =>
                s.id === currentStyle.id
                  ? { ...s, ...values, updatedAt: new Date().toISOString() }
                  : s,
              );
              setStyles(updatedStyles);
              message.success('更新しました');
              setEditModalVisible(false);
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="スタイル名"
                  rules={[
                    { required: true, message: 'スタイル名を入力してください' },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="category"
                  label="カテゴリ"
                  rules={[
                    { required: true, message: 'カテゴリを選択してください' },
                  ]}
                >
                  <Select>
                    <Option value="business">Business</Option>
                    <Option value="media">Media</Option>
                    <Option value="blog">Blog</Option>
                    <Option value="custom">Custom</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="description"
              label="説明"
              rules={[{ required: true, message: '説明を入力してください' }]}
            >
              <TextArea rows={2} />
            </Form.Item>

            <Collapse defaultActiveKey={['tone']} style={{ marginTop: 24 }}>
              {Object.entries(styleCategories).map(
                ([categoryKey, category]) => (
                  <Panel
                    header={
                      <Space>
                        {category.icon}
                        <Text strong>{category.title}</Text>
                      </Space>
                    }
                    key={categoryKey}
                  >
                    {Object.entries(category.items).map(([itemKey, item]) =>
                      renderSettingItem(
                        categoryKey,
                        itemKey,
                        item,
                        currentStyle?.settings[categoryKey]?.[itemKey],
                        value => {
                          const currentSettings =
                            editForm.getFieldValue('settings') || {};
                          editForm.setFieldsValue({
                            settings: {
                              ...currentSettings,
                              [categoryKey]: {
                                ...currentSettings[categoryKey],
                                [itemKey]: value,
                              },
                            },
                          });
                        },
                      ),
                    )}
                  </Panel>
                ),
              )}
            </Collapse>

            <Form.Item style={{ marginTop: 24 }}>
              <Space>
                <Button
                  type="primary"
                  onClick={async () => {
                    try {
                      const values = await editForm.validateFields();
                      const payload = {
                        name: values.name,
                        description: values.description,
                        // フォームベースの設定はダミー扱いのまま。将来的にスキーママッピングを実装
                      };
                      const updated = await v2StylesApi.updateStyle(
                        currentStyle.id,
                        payload,
                      );
                      setStyles(prev =>
                        prev.map(s =>
                          s.id === currentStyle.id
                            ? {
                                ...s,
                                name: updated.name,
                                description: updated.description || '',
                                updatedAt:
                                  updated.updated_at ||
                                  new Date().toISOString(),
                              }
                            : s,
                        ),
                      );
                      message.success('更新しました');
                      setEditModalVisible(false);
                      editForm.resetFields();
                    } catch (e) {
                      if (e?.errorFields) return; // バリデーションエラー
                      message.error(
                        e?.response?.data?.detail || '更新に失敗しました',
                      );
                    }
                  }}
                >
                  保存
                </Button>
                <Button
                  onClick={() => {
                    setEditModalVisible(false);
                    editForm.resetFields();
                  }}
                >
                  キャンセル
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* プレビューモーダル */}
        <Modal
          title={`スタイルプレビュー: ${currentStyle?.name}`}
          open={previewModalVisible}
          onCancel={() => setPreviewModalVisible(false)}
          footer={[
            <Button
              key="cfg"
              icon={<SettingOutlined />}
              onClick={() => {
                setPreviewModalVisible(false);
                openConfigForStyle(currentStyle);
              }}
            >
              設定編集
            </Button>,
            <Button
              key="yaml"
              icon={<CodeOutlined />}
              onClick={() => {
                openYamlModal(currentStyle);
                setPreviewModalVisible(false);
              }}
            >
              YAML表示
            </Button>,
            <Button key="close" onClick={() => setPreviewModalVisible(false)}>
              閉じる
            </Button>,
          ]}
          width={800}
        >
          {currentStyle && (
            <div>
              <Descriptions
                bordered
                column={1}
                size="small"
                style={{ marginBottom: 16 }}
              >
                <Descriptions.Item label="スタイル名">
                  {currentStyle.name}
                </Descriptions.Item>
                <Descriptions.Item label="メモ">
                  {currentStyle.description}
                </Descriptions.Item>
                <Descriptions.Item label="カテゴリ">
                  <Tag>{currentStyle.category}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="ソースURL">
                  <a
                    href={currentStyle.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {currentStyle.sourceUrl}
                  </a>
                </Descriptions.Item>
                <Descriptions.Item label="作成日">
                  {moment(currentStyle.createdAt).format('YYYY/MM/DD HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label="更新日">
                  {moment(currentStyle.updatedAt).format('YYYY/MM/DD HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label="使用回数">
                  {currentStyle.usage}回
                </Descriptions.Item>
              </Descriptions>

              <Tabs defaultActiveKey="tone" style={{ marginTop: 16 }}>
                {Object.entries(styleCategories).map(
                  ([categoryKey, category]) => (
                    <TabPane
                      tab={
                        <Space>
                          {category.icon}
                          {category.title}
                        </Space>
                      }
                      key={categoryKey}
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {Object.entries(category.items).map(
                          ([itemKey, item]) => {
                            const value =
                              currentStyle.settings[categoryKey]?.[itemKey];
                            return (
                              <div key={itemKey} style={{ marginBottom: 16 }}>
                                <Text strong>{item.label}: </Text>
                                {item.type === 'slider' ? (
                                  <span>
                                    <Progress
                                      percent={value}
                                      size="small"
                                      style={{
                                        width: 200,
                                        display: 'inline-block',
                                        marginLeft: 8,
                                      }}
                                    />
                                    <Text style={{ marginLeft: 8 }}>
                                      {value}%
                                    </Text>
                                  </span>
                                ) : (
                                  <Tag color="blue">{value}</Tag>
                                )}
                              </div>
                            );
                          },
                        )}
                      </Space>
                    </TabPane>
                  ),
                )}
              </Tabs>
            </div>
          )}
        </Modal>

        {/* 設定編集モーダル（主要項目の最小実装） */}
        <Modal
          title="スタイル設定を編集"
          open={configModalVisible}
          onCancel={() => setConfigModalVisible(false)}
          footer={null}
          width="86vw"
          className="style-modal-fixed"
          centered
          style={{ top: 8 }}
          bodyStyle={{ overflow: 'hidden' }}
          closable={false}
          maskClosable={false}
          keyboard={false}
        >
          <Form
            form={configForm}
            layout="vertical"
            className="v2-form-underline"
            onValuesChange={() => {
              refreshYamlPreview();
            }}
          >
            {(() => {
              // スタイルプレビュー（生成STEP1と同等の計算式を適用）
              const StylePreviewPanel = () => {
                const getVal = name => configForm.getFieldValue(name);
                const cfg =
                  draftConfig ||
                  (currentStyle && currentStyle.style_config) ||
                  {};
                const parseRange = s => {
                  if (!s || typeof s !== 'string') return [0, 0, 0];
                  const m = s.split('-').map(x => Number(String(x).trim()));
                  if (m.length >= 2) {
                    const a = Number.isFinite(m[0]) ? m[0] : 0;
                    const b = Number.isFinite(m[1]) ? m[1] : a;
                    const lo = Math.min(a, b);
                    const hi = Math.max(a, b);
                    const mid = (lo + hi) / 2;
                    return [lo, hi, mid];
                  }
                  const v = Number.isFinite(m[0]) ? m[0] : 0;
                  return [v, v, v];
                };
                const h2nStr = getVal('h2_typical');
                const h3nStr = getVal('h3_typical');
                const h2bStr = getVal('h2_body_chars_typical');
                const h3bStr = getVal('h3_body_chars_typical');
                const [h2Min, h2Max] = parseRange(h2nStr);
                const [h3Min, h3Max, h3Mid] = parseRange(h3nStr);
                const [h2BodyMin, h2BodyMax] = parseRange(h2bStr);
                const [h3BodyMin, h3BodyMax, h3BodyMid] = parseRange(h3bStr);
                const ratioMaxUse = 0.8;
                let totalPref = '—';
                if (h2Max && h2BodyMax) {
                  const estMinBase =
                    h2Min * (h2BodyMin + 1 * h3Mid * h3BodyMid);
                  const estMaxBase =
                    h2Max * (h2BodyMax + ratioMaxUse * h3Max * h3BodyMax);
                  const minEst = Math.max(
                    0,
                    Math.round(estMinBase / 100) * 100,
                  );
                  const maxEst = Math.max(
                    minEst,
                    Math.round(estMaxBase / 100) * 100,
                  );
                  totalPref = `${minEst.toLocaleString()}-${maxEst.toLocaleString()}`;
                }
                const formatRange = s => {
                  if (!s || typeof s !== 'string') return '—';
                  const parts = s.split('-');
                  if (parts.length === 1) {
                    const n = Number(parts[0]);
                    return Number.isNaN(n) ? s : n.toLocaleString();
                  }
                  const a = Number(parts[0]);
                  const b = Number(parts[1]);
                  const fa = Number.isNaN(a) ? parts[0] : a.toLocaleString();
                  const fb = Number.isNaN(b) ? parts[1] : b.toLocaleString();
                  return `${fa}-${fb}`;
                };
                return (
                  <div>
                    <div className="style-kpis">
                      <div className="style-kpi">
                        <div className="label">想定文字数（記事全体）</div>
                        <div className="value">
                          {totalPref}
                          <span className="unit">文字</span>
                        </div>
                      </div>
                      <div className="style-kpi">
                        <div className="label">です/ます比率</div>
                        <div className="value">
                          —<span className="unit">%</span>
                        </div>
                      </div>
                    </div>
                    <div className="style-sep"></div>
                    <div className="style-grid">
                      <div className="head">項目</div>
                      <div className="head">H2</div>
                      <div className="head">H3</div>
                      <div className="cell left">
                        <div className="metric">基本設置数</div>
                      </div>
                      <div className="cell">
                        <div className="num">
                          {h2nStr || '—'}
                          <span className="unit">個</span>
                        </div>
                      </div>
                      <div className="cell">
                        <div className="num">
                          {h3nStr || '—'}
                          <span className="unit">個</span>
                        </div>
                      </div>
                      <div className="cell left">
                        <div className="metric">見出し文字数</div>
                      </div>
                      <div className="cell">
                        <div className="num">
                          {formatRange(getVal('h2_chars_typical'))}
                          <span className="unit">文字</span>
                        </div>
                      </div>
                      <div className="cell">
                        <div className="num">
                          {formatRange(getVal('h3_chars_typical'))}
                          <span className="unit">文字</span>
                        </div>
                      </div>
                      <div className="cell left">
                        <div className="metric">見出し直下の文字数</div>
                      </div>
                      <div className="cell">
                        <div className="num">
                          {formatRange(h2bStr)}
                          <span className="unit">文字</span>
                        </div>
                      </div>
                      <div className="cell">
                        <div className="num">
                          {formatRange(h3bStr)}
                          <span className="unit">文字</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              };
              return null;
            })()}
            {(() => {
              const talcPref = Form.useWatch('talc_pref', configForm);
              const talcMin = Form.useWatch('talc_min', configForm);
              const talcMax = Form.useWatch('talc_max', configForm);
              const h2Recommended = React.useMemo(() => {
                const parseRange = s => {
                  if (!s) return null;
                  const m = String(s).match(/(\d+)\s*-\s*(\d+)/);
                  if (!m) return null;
                  return [parseInt(m[1], 10), parseInt(m[2], 10)].sort(
                    (a, b) => a - b,
                  );
                };
                let minChars = null;
                let maxChars = null;
                const pref = parseRange(talcPref);
                if (pref) {
                  [minChars, maxChars] = pref;
                } else if (
                  typeof talcMin === 'number' &&
                  typeof talcMax === 'number' &&
                  talcMax > 0
                ) {
                  minChars = talcMin;
                  maxChars = talcMax;
                }
                if (!minChars || !maxChars) return null;
                const lower = Math.max(2, Math.floor(minChars / 800));
                const upper = Math.max(lower, Math.ceil(maxChars / 600));
                return `${lower}-${upper}`;
              }, [talcPref, talcMin, talcMax]);
              return (
                <Row gutter={24} wrap>
                  <Col xs={24} md={14}>
                    <div
                      style={{
                        maxHeight: 'calc(100vh - 220px)',
                        overflowY: 'auto',
                        paddingRight: 8,
                      }}
                    >
                      <Collapse
                        defaultActiveKey={[
                          'basic',
                          'meta',
                          'headings',
                          'rhythm',
                          'style',
                          'rules',
                        ]}
                      >
                        <Panel
                          header={<Text strong>基本情報</Text>}
                          key="basic"
                        >
                          <Form.Item name="name" label="スタイル名">
                            <Input placeholder="スタイル名を入力" />
                          </Form.Item>
                          <Form.Item name="description" label="メモ">
                            <Input placeholder="専門性は高めだが読みやすさ重視" />
                          </Form.Item>
                          <Form.Item name="language" label="言語">
                            <Select allowClear>
                              <Option value="ja">日本語</Option>
                              <Option value="en">英語</Option>
                            </Select>
                          </Form.Item>
                          {(() => {
                            const src =
                              getNested(
                                currentStyle?.style_config,
                                'metadata.source_url',
                              ) || currentStyle?.sourceUrl;
                            return src ? (
                              <Form.Item label="解析元URL">
                                <a
                                  href={src}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {src}
                                </a>
                              </Form.Item>
                            ) : null;
                          })()}
                        </Panel>
                        <Panel
                          header={<Text strong>見出し設定</Text>}
                          key="meta"
                        >
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item name="h2_typical" label="H2の個数目安">
                                <Input
                                  placeholder="例: 6-10"
                                  inputMode="numeric"
                                  pattern="[0-9\-]+"
                                  onKeyPress={e => {
                                    if (!/[0-9-]/.test(e.key))
                                      e.preventDefault();
                                  }}
                                  onBeforeInput={e => {
                                    const ch = e.data || '';
                                    if (/^[０-９－]$/.test(ch)) {
                                      e.preventDefault();
                                      const half = String.fromCharCode(
                                        ch.charCodeAt(0) - 0xfee0,
                                      );
                                      const cur = e.target.value || '';
                                      const start =
                                        e.target.selectionStart || cur.length;
                                      const end =
                                        e.target.selectionEnd || cur.length;
                                      const next =
                                        cur.slice(0, start) +
                                        half +
                                        cur.slice(end);
                                      configForm.setFieldsValue({
                                        h2_typical: next,
                                      });
                                    }
                                  }}
                                  onChange={e => {
                                    refreshYamlPreview();
                                  }}
                                  onBlur={e => {
                                    const val = (e.target.value || '').replace(
                                      /[^0-9-]/g,
                                      '',
                                    );
                                    if (val !== e.target.value) {
                                      configForm.setFieldsValue({
                                        h2_typical: val,
                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item name="h3_typical" label="H3の個数目安">
                                <Input
                                  placeholder="例: 2-4"
                                  inputMode="numeric"
                                  pattern="[0-9\-]+"
                                  onKeyPress={e => {
                                    if (!/[0-9-]/.test(e.key))
                                      e.preventDefault();
                                  }}
                                  onBeforeInput={e => {
                                    const ch = e.data || '';
                                    if (/^[０-９－]$/.test(ch)) {
                                      e.preventDefault();
                                      const half = String.fromCharCode(
                                        ch.charCodeAt(0) - 0xfee0,
                                      );
                                      const cur = e.target.value || '';
                                      const start =
                                        e.target.selectionStart || cur.length;
                                      const end =
                                        e.target.selectionEnd || cur.length;
                                      const next =
                                        cur.slice(0, start) +
                                        half +
                                        cur.slice(end);
                                      configForm.setFieldsValue({
                                        h3_typical: next,
                                      });
                                    }
                                  }}
                                  onChange={e => {
                                    refreshYamlPreview();
                                  }}
                                  onBlur={e => {
                                    const val = (e.target.value || '').replace(
                                      /[^0-9-]/g,
                                      '',
                                    );
                                    if (val !== e.target.value) {
                                      configForm.setFieldsValue({
                                        h3_typical: val,
                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Divider style={{ margin: '16px 0' }} />
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item
                                name="h2_chars_typical"
                                label="H2見出しの文字数"
                                tooltip="見出し自体の文字数（例: 16-28）"
                              >
                                <Input
                                  placeholder="例: 16-28"
                                  inputMode="numeric"
                                  pattern="[0-9\-]+"
                                  onKeyPress={e => {
                                    if (!/[0-9-]/.test(e.key))
                                      e.preventDefault();
                                  }}
                                  onChange={e => {
                                    refreshYamlPreview();
                                  }}
                                  onBlur={e => {
                                    const val = (e.target.value || '').replace(
                                      /[^0-9-]/g,
                                      '',
                                    );
                                    if (val !== e.target.value) {
                                      configForm.setFieldsValue({
                                        h2_chars_typical: val,
                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                name="h3_chars_typical"
                                label="H3見出しの文字数"
                                tooltip="見出し自体の文字数（例: 14-24）"
                              >
                                <Input
                                  placeholder="例: 14-24"
                                  inputMode="numeric"
                                  pattern="[0-9\-]+"
                                  onKeyPress={e => {
                                    if (!/[0-9-]/.test(e.key))
                                      e.preventDefault();
                                  }}
                                  onChange={e => {
                                    refreshYamlPreview();
                                  }}
                                  onBlur={e => {
                                    const val = (e.target.value || '').replace(
                                      /[^0-9-]/g,
                                      '',
                                    );
                                    if (val !== e.target.value) {
                                      configForm.setFieldsValue({
                                        h3_chars_typical: val,
                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item
                                name="h2_body_chars_typical"
                                label="H2直下の本文の文字数目安"
                                tooltip="最大800文字まで設定可能"
                                rules={[
                                  {
                                    validator: (_, value) => {
                                      if (!value) return Promise.resolve();
                                      const nums = value.match(/\d+/g);
                                      if (
                                        nums &&
                                        nums.some(n => parseInt(n, 10) > 800)
                                      ) {
                                        return Promise.reject(
                                          new Error('最大800文字までです'),
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                              >
                                <Input
                                  placeholder="例: 400-800"
                                  inputMode="numeric"
                                  pattern="[0-9\-]+"
                                  onKeyPress={e => {
                                    if (!/[0-9-]/.test(e.key))
                                      e.preventDefault();
                                  }}
                                  onBeforeInput={e => {
                                    const ch = e.data || '';
                                    if (/^[０-９－]$/.test(ch)) {
                                      e.preventDefault();
                                      const half = String.fromCharCode(
                                        ch.charCodeAt(0) - 0xfee0,
                                      );
                                      const cur = e.target.value || '';
                                      const start =
                                        e.target.selectionStart || cur.length;
                                      const end =
                                        e.target.selectionEnd || cur.length;
                                      const next =
                                        cur.slice(0, start) +
                                        half +
                                        cur.slice(end);
                                      configForm.setFieldsValue({
                                        h2_body_chars_typical: next,
                                      });
                                    }
                                  }}
                                  onChange={e => {
                                    refreshYamlPreview();
                                  }}
                                  onBlur={e => {
                                    let val = (e.target.value || '').replace(
                                      /[^0-9-]/g,
                                      '',
                                    );
                                    // 最大800に制限
                                    const nums = val.match(/\d+/g);
                                    if (nums) {
                                      const capped = nums.map(n =>
                                        Math.min(parseInt(n, 10), 800),
                                      );
                                      if (
                                        val.includes('-') &&
                                        capped.length === 2
                                      ) {
                                        val = `${capped[0]}-${capped[1]}`;
                                      } else if (capped.length >= 1) {
                                        val = val.includes('-')
                                          ? `${capped[0]}-${capped[capped.length - 1] || capped[0]}`
                                          : String(capped[0]);
                                      }
                                    }
                                    if (val !== e.target.value) {
                                      configForm.setFieldsValue({
                                        h2_body_chars_typical: val,
                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                name="h3_body_chars_typical"
                                label="H3直下の本文の文字数目安"
                                tooltip="最大800文字まで設定可能"
                                rules={[
                                  {
                                    validator: (_, value) => {
                                      if (!value) return Promise.resolve();
                                      const nums = value.match(/\d+/g);
                                      if (
                                        nums &&
                                        nums.some(n => parseInt(n, 10) > 800)
                                      ) {
                                        return Promise.reject(
                                          new Error('最大800文字までです'),
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                              >
                                <Input
                                  placeholder="例: 300-500"
                                  inputMode="numeric"
                                  pattern="[0-9\-]+"
                                  onKeyPress={e => {
                                    if (!/[0-9-]/.test(e.key))
                                      e.preventDefault();
                                  }}
                                  onBeforeInput={e => {
                                    const ch = e.data || '';
                                    if (/^[０-９－]$/.test(ch)) {
                                      e.preventDefault();
                                      const half = String.fromCharCode(
                                        ch.charCodeAt(0) - 0xfee0,
                                      );
                                      const cur = e.target.value || '';
                                      const start =
                                        e.target.selectionStart || cur.length;
                                      const end =
                                        e.target.selectionEnd || cur.length;
                                      const next =
                                        cur.slice(0, start) +
                                        half +
                                        cur.slice(end);
                                      configForm.setFieldsValue({
                                        h3_body_chars_typical: next,
                                      });
                                    }
                                  }}
                                  onChange={e => {
                                    refreshYamlPreview();
                                  }}
                                  onBlur={e => {
                                    let val = (e.target.value || '').replace(
                                      /[^0-9-]/g,
                                      '',
                                    );
                                    // 最大800に制限
                                    const nums = val.match(/\d+/g);
                                    if (nums) {
                                      const capped = nums.map(n =>
                                        Math.min(parseInt(n, 10), 800),
                                      );
                                      if (
                                        val.includes('-') &&
                                        capped.length === 2
                                      ) {
                                        val = `${capped[0]}-${capped[1]}`;
                                      } else if (capped.length >= 1) {
                                        val = val.includes('-')
                                          ? `${capped[0]}-${capped[capped.length - 1] || capped[0]}`
                                          : String(capped[0]);
                                      }
                                    }
                                    if (val !== e.target.value) {
                                      configForm.setFieldsValue({
                                        h3_body_chars_typical: val,
                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item
                                name="ui_allow_interrogative"
                                label="見出しに疑問文を使う"
                                valuePropName="checked"
                              >
                                <Switch />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                name="ui_heading_symbols"
                                label="見出しの許可記号"
                              >
                                <Checkbox.Group
                                  options={[
                                    {
                                      label: 'クエスチョン（？）',
                                      value: 'question',
                                    },
                                    {
                                      label: 'かっこ（（）[]）',
                                      value: 'brackets',
                                    },
                                    { label: 'コロン（:）', value: 'colon' },
                                  ]}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Panel>
                        <Panel
                          header={<Text strong>つなぎ言葉（接続句）</Text>}
                          key="transitions"
                        >
                          <Space
                            direction="vertical"
                            style={{ width: '100%' }}
                            size="middle"
                          >
                            {/* 禁止 */}
                            <div>
                              <Text
                                strong
                                style={{
                                  color: '#ff4d4f',
                                  marginBottom: 8,
                                  display: 'block',
                                }}
                              >
                                禁止（使用しない）
                              </Text>
                              <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                placeholder="使用しない接続詞を選択..."
                                value={forbiddenWords}
                                onChange={handleForbiddenWordsChange}
                                options={ALL_CONNECTIVE_WORDS.filter(
                                  w =>
                                    !minimizeWords.includes(w) &&
                                    !moderateWords.includes(w),
                                ).map(w => ({ label: w, value: w }))}
                                maxTagCount="responsive"
                              />
                            </div>

                            {/* 控えめ */}
                            <div>
                              <Text
                                strong
                                style={{
                                  color: '#faad14',
                                  marginBottom: 8,
                                  display: 'block',
                                }}
                              >
                                控えめ（なるべく使わない）
                              </Text>
                              <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                placeholder="控えめに使用する接続詞を選択..."
                                value={minimizeWords}
                                onChange={handleMinimizeWordsChange}
                                options={ALL_CONNECTIVE_WORDS.filter(
                                  w =>
                                    !forbiddenWords.includes(w) &&
                                    !moderateWords.includes(w),
                                ).map(w => ({ label: w, value: w }))}
                                maxTagCount="responsive"
                              />
                            </div>

                            {/* 適度 */}
                            <div>
                              <Text
                                strong
                                style={{
                                  color: '#52c41a',
                                  marginBottom: 8,
                                  display: 'block',
                                }}
                              >
                                適度（必要に応じて使用）
                              </Text>
                              <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                placeholder="必要に応じて使用する接続詞を選択..."
                                value={moderateWords}
                                onChange={handleModerateWordsChange}
                                options={ALL_CONNECTIVE_WORDS.filter(
                                  w =>
                                    !forbiddenWords.includes(w) &&
                                    !minimizeWords.includes(w),
                                ).map(w => ({ label: w, value: w }))}
                                maxTagCount="responsive"
                              />
                            </div>

                            {/* Hidden field for form data */}
                            <Form.Item name="connective_words_data" hidden>
                              <Input type="hidden" />
                            </Form.Item>
                          </Space>
                        </Panel>
                        <Panel
                          header={<Text strong>文末表現</Text>}
                          key="sentence_endings"
                        >
                          <Row gutter={[8, 8]}>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_masen_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_masen_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ません
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_deshou_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_deshou_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                でしょう
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_desune_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_desune_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ですね
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_desuyo_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_desuyo_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ですよ
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_masune_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_masune_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ますね
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_masuyo_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_masuyo_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ますよ
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_deshita_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_deshita_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                でした
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_kudasai_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_kudasai_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ください
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_shimashou_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_shimashou_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                しましょう
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_mashita_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_mashita_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ました
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_mashou_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_mashou_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ましょう
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_desuka_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_desuka_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ですか
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_masuka_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_masuka_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ますか
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_taigen_dome_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_taigen_dome_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                体言止め
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_ndesu_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_ndesu_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                んです
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_masenka_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_masenka_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ませんか
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_deshouka_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_deshouka_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                でしょうか
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_masuyoune_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_masuyoune_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ますよね
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_desuyoune_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_desuyoune_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                ですよね
                              </Checkbox>
                            </Col>
                            <Col span={8}>
                              <Checkbox
                                checked={
                                  configForm.getFieldValue(
                                    'sentence_ending_to_omoimasu_enabled',
                                  ) || false
                                }
                                onChange={e => {
                                  configForm.setFieldsValue({
                                    sentence_ending_to_omoimasu_enabled:
                                      e.target.checked,
                                  });
                                  refreshYamlPreview();
                                }}
                              >
                                と思います
                              </Checkbox>
                            </Col>
                          </Row>
                          {/* Hidden Form.Items for form state management */}
                          <Form.Item
                            name="sentence_ending_masen_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_deshou_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_desune_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_desuyo_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_masune_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_masuyo_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_to_omoimasu_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_deshita_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_kudasai_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_shimashou_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_mashita_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_mashou_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_desuka_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_masuka_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_taigen_dome_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_ndesu_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_masenka_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_deshouka_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_masuyoune_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                          <Form.Item
                            name="sentence_ending_desuyoune_enabled"
                            valuePropName="checked"
                            hidden
                          >
                            <Checkbox />
                          </Form.Item>
                        </Panel>
                        <Panel
                          header={<Text strong>文体（直感調整）</Text>}
                          key="style"
                        >
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item label="丁寧さ" name="politeness">
                                <Slider
                                  min={0}
                                  max={100}
                                  tooltip={{ open: false }}
                                  marks={{ 0: '常体', 100: 'です/ます' }}
                                  onChange={() => refreshYamlPreview()}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="センテンスの長さ"
                                name="sentence_length"
                              >
                                <Slider
                                  min={0}
                                  max={100}
                                  tooltip={{ open: false }}
                                  marks={{ 0: '短い', 100: '長い' }}
                                  onChange={() => refreshYamlPreview()}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item label="トーン" name="energy">
                                <Slider
                                  min={0}
                                  max={100}
                                  tooltip={{ open: false }}
                                  marks={{ 0: '落ち着き', 100: '推進/強調' }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="語彙性" name="info_density">
                                <Slider
                                  min={0}
                                  max={100}
                                  tooltip={{ open: false }}
                                  marks={{ 0: '少', 100: '多' }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item label="中立性" name="neutrality">
                                <Slider
                                  min={0}
                                  max={100}
                                  tooltip={{ open: false }}
                                  marks={{ 0: '評価控え', 100: '評価する' }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="専門性" name="expertise">
                                <Slider
                                  min={0}
                                  max={100}
                                  tooltip={{ open: false }}
                                  marks={{ 0: '入門的', 100: '専門的' }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Panel>
                        <Panel
                          header={<Text strong>装飾（箇条書き/表）</Text>}
                          key="decoration"
                        >
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item
                                label="箇条書きの頻度"
                                name="bullets_preset"
                              >
                                <Select
                                  options={[
                                    {
                                      label: 'ほとんどなし',
                                      value: 'very_low',
                                    },
                                    { label: '少なめ', value: 'low' },
                                    {
                                      label: 'やや少なめ',
                                      value: 'slightly_low',
                                    },
                                    { label: '標準', value: 'medium' },
                                    {
                                      label: 'やや多め',
                                      value: 'slightly_high',
                                    },
                                    { label: '多め', value: 'high' },
                                    { label: 'かなり多め', value: 'very_high' },
                                  ]}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="表の頻度" name="tables_preset">
                                <Select
                                  options={[
                                    {
                                      label: 'ほとんどなし',
                                      value: 'very_low',
                                    },
                                    { label: '少なめ', value: 'low' },
                                    {
                                      label: 'やや少なめ',
                                      value: 'slightly_low',
                                    },
                                    { label: '標準', value: 'medium' },
                                    {
                                      label: 'やや多め',
                                      value: 'slightly_high',
                                    },
                                    { label: '多め', value: 'high' },
                                    { label: 'かなり多め', value: 'very_high' },
                                  ]}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Divider style={{ margin: '12px 0' }}>
                            箇条書きルール
                          </Divider>
                          <Row gutter={12}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                label="入れ子の深さ（max）"
                                name="bullets_max_nesting"
                                tooltip="推奨: 0〜2"
                              >
                                <InputNumber
                                  min={0}
                                  max={5}
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                label="1項目の最大文字数"
                                name="bullets_item_max_chars"
                                tooltip="推奨: 80"
                              >
                                <InputNumber
                                  min={20}
                                  max={200}
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={12}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                label="行末の句点を付けない"
                                name="bullets_terminal_punct_disallowed"
                                valuePropName="checked"
                              >
                                <Switch />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                label="並列性を揃える（語尾/形式）"
                                name="bullets_parallelism_required"
                                valuePropName="checked"
                              >
                                <Switch />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Panel>
                        <Panel
                          header={<Text strong>カスタムルール（任意）</Text>}
                          key="rules"
                        >
                          <Space
                            direction="vertical"
                            style={{ width: '100%' }}
                            size="middle"
                          >
                            {/* 追加指示（構成案・本文） */}
                            <Row gutter={12}>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label="追加指示（構成案）"
                                  name="additional_instructions_outline"
                                  extra="構成案生成時の追加指示（最大1,000文字）"
                                >
                                  <TextArea
                                    rows={3}
                                    maxLength={1000}
                                    showCount
                                    placeholder="例：各見出しに必ず具体例を含める。数値データを優先的に配置する。"
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label="追加指示（本文）"
                                  name="additional_instructions_content"
                                  extra="本文執筆時の追加指示（最大1,000文字）"
                                >
                                  <TextArea
                                    rows={3}
                                    maxLength={1000}
                                    showCount
                                    placeholder="例：専門用語は必ず初出時に解説する。数値データを積極的に引用する。"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>

                            <Row gutter={12}>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label="禁止表現"
                                  name="avoid_text"
                                  extra="1行1ルール（最大500文字）"
                                >
                                  <TextArea
                                    rows={4}
                                    maxLength={500}
                                    showCount
                                    placeholder="例:&#10;非常に非常に&#10;〜と言えるでしょう。でしょう。"
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label="置換ルール"
                                  name="rewrite_text"
                                  extra="A=>B形式、1行1ルール（最大500文字）"
                                >
                                  <TextArea
                                    rows={4}
                                    maxLength={500}
                                    showCount
                                    placeholder="例:&#10;非常に=>かなり&#10;〜だと思います=>〜です"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Space>
                        </Panel>
                        
                        {/* 調査フィルター */}
                        <Panel
                          header={
                            <Space>
                              <span>調査フィルター</span>
                              <Tooltip title="Web調査時のドメイン制限">
                                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                              </Tooltip>
                            </Space>
                          }
                          key="domain_filter"
                        >
                          <Space direction="vertical" style={{ width: '100%' }} size={16}>
                            <Form.Item
                              label="フィルターモード"
                              name="domain_filter_mode"
                            >
                              <Radio.Group>
                                <Radio value="disabled">無効</Radio>
                                <Radio value="whitelist">ホワイトリスト（指定ドメインのみ検索）</Radio>
                                <Radio value="blacklist">ブラックリスト（指定ドメインを除外）</Radio>
                              </Radio.Group>
                            </Form.Item>

                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.domain_filter_mode !== cur.domain_filter_mode}>
                              {({ getFieldValue }) => {
                                const mode = getFieldValue('domain_filter_mode') || 'disabled';
                                const whitelistOpacity = mode === 'whitelist' ? 1 : 0.4;
                                const blacklistOpacity = mode === 'blacklist' ? 1 : 0.4;
                                return (
                                  <Row gutter={12}>
                                    <Col xs={24} md={12} style={{ opacity: whitelistOpacity, transition: 'opacity 0.2s' }}>
                                      <Form.Item
                                        label="ホワイトリスト"
                                        name="domain_filter_whitelist"
                                        extra="1行1ドメイン（最大20件）"
                                      >
                                        <TextArea
                                          rows={5}
                                          placeholder={"example.com\nwikipedia.org\n.gov"}
                                          disabled={mode !== 'whitelist'}
                                          onBlur={(e) => {
                                            const processed = processDomainInput(e.target.value);
                                            configForm.setFieldsValue({ domain_filter_whitelist: processed });
                                          }}
                                        />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12} style={{ opacity: blacklistOpacity, transition: 'opacity 0.2s' }}>
                                      <Form.Item
                                        label="ブラックリスト"
                                        name="domain_filter_blacklist"
                                        extra="1行1ドメイン（最大20件）"
                                      >
                                        <TextArea
                                          rows={5}
                                          placeholder={"reddit.com\npinterest.com\nquora.com"}
                                          disabled={mode !== 'blacklist'}
                                          onBlur={(e) => {
                                            const processed = processDomainInput(e.target.value);
                                            configForm.setFieldsValue({ domain_filter_blacklist: processed });
                                          }}
                                        />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                );
                              }}
                            </Form.Item>

                            <div
                              style={{
                                background: '#fffbe6',
                                border: '1px solid #fff1b8',
                                borderRadius: 6,
                                padding: '8px 12px',
                                fontSize: 12,
                                color: '#8b7355',
                              }}
                            >
                              💡 URLを貼り付けるとドメインを自動抽出 / TLD指定可（.gov, .edu等） / 選択モードのリストのみ有効
                            </div>
                          </Space>
                        </Panel>
                      </Collapse>
                    </div>
                  </Col>
                  <Col xs={24} md={10}>
                    <div
                      style={{
                        position: 'sticky',
                        top: 8,
                        maxHeight: 'calc(100vh - 164px)',
                        overflowY: 'auto',
                        paddingLeft: 8,
                        paddingBottom: 96,
                      }}
                    >
                      <Card title="スタイルプレビュー" size="small">
                        {(() => {
                          const cfg =
                            draftConfig ||
                            (currentStyle && currentStyle.style_config) ||
                            {};
                        })()}
                        {(() => {
                          // YAMLのみを単一ソースとして参照
                          const cfg =
                            draftConfig ||
                            (currentStyle && currentStyle.style_config) ||
                            {};

                          const toNum = v => {
                            const n = Number(v);
                            return Number.isFinite(n) ? n : null;
                          };
                          const clamp = v => Math.max(0, Math.min(100, v));
                          const parseRange = s => {
                            if (!s || typeof s !== 'string') return [0, 0, 0];
                            const m = s
                              .split('-')
                              .map(x => Number(String(x).trim()));
                            if (m.length >= 2) {
                              const a = Number.isFinite(m[0]) ? m[0] : 0;
                              const b = Number.isFinite(m[1]) ? m[1] : a;
                              const lo = Math.min(a, b);
                              const hi = Math.max(a, b);
                              const mid = (lo + hi) / 2;
                              return [lo, hi, mid];
                            }
                            const v = Number.isFinite(m[0]) ? m[0] : 0;
                            return [v, v, v];
                          };
                          const formatRange = s => {
                            if (!s || typeof s !== 'string') return '—';
                            const parts = s.split('-');
                            if (parts.length === 1) {
                              const n = Number(parts[0]);
                              return Number.isNaN(n) ? s : n.toLocaleString();
                            }
                            const a = Number(parts[0]);
                            const b = Number(parts[1]);
                            const fa = Number.isNaN(a)
                              ? parts[0]
                              : a.toLocaleString();
                            const fb = Number.isNaN(b)
                              ? parts[1]
                              : b.toLocaleString();
                            return `${fa}-${fb}`;
                          };

                          // YAML → 値抽出
                          const h2n = pickFirst(cfg, [
                            'headings_style.counts.h2_per_article.typical',
                            'headings.h2_per_article.typical',
                          ]);
                          const h3n = pickFirst(cfg, [
                            'headings_style.counts.h3_per_h2.typical',
                            'headings.h3_per_h2.typical',
                          ]);
                          const h2h = pickFirst(cfg, [
                            'headings_style.length_policy.h2_chars.typical',
                            'headings.length_policy.h2_chars.typical',
                          ]);
                          const h3h = pickFirst(cfg, [
                            'headings_style.length_policy.h3_chars.typical',
                            'headings.length_policy.h3_chars.typical',
                          ]);
                          const h2b = pickFirst(cfg, [
                            'headings_style.length_policy.h2_body_chars.typical',
                            'headings.length_policy.h2_body_chars.typical',
                          ]);
                          const h3b = pickFirst(cfg, [
                            'headings_style.length_policy.h3_body_chars.typical',
                            'headings.length_policy.h3_body_chars.typical',
                          ]);

                          // 文字数見積り（STEP1準拠の式）
                          const [, h2Max] = parseRange(String(h2n || ''));
                          const [, h3Max, h3Mid] = parseRange(
                            String(h3n || ''),
                          );
                          const [h2BodyMin, h2BodyMax] = parseRange(
                            String(h2b || ''),
                          );
                          const [, , h3BodyMid] = parseRange(String(h3b || ''));
                          const ratioMaxUse = 0.8;
                          let totalPref = '—';
                          if (h2Max && h2BodyMax) {
                            const [h2Min] = parseRange(String(h2n || ''));
                            const estMinBase =
                              h2Min * (h2BodyMin + 1 * h3Mid * h3BodyMid);
                            const estMaxBase =
                              h2Max *
                              (h2BodyMax +
                                ratioMaxUse *
                                  h3Max *
                                  (parseRange(String(h3b || ''))[1] || 0));
                            const minEst = Math.max(
                              0,
                              Math.round(estMinBase / 100) * 100,
                            );
                            const maxEst = Math.max(
                              minEst,
                              Math.round(estMaxBase / 100) * 100,
                            );
                            totalPref = `${minEst.toLocaleString()}-${maxEst.toLocaleString()}`;
                          }

                          // スライダーバー用の値
                          const sentenceLenAvgStr =
                            pickFirst(cfg, [
                              'prose_style.rhythm.sentence_length_chars.avg',
                              'rhythm.sentence_length_chars.avg',
                            ]) || '';
                          const parts = String(sentenceLenAvgStr)
                            .split('-')
                            .map(x => Number(String(x).trim()));
                          const sentenceLenAvg =
                            parts.length >= 2
                              ? (Number.isNaN(parts[0])
                                  ? 0
                                  : parts[0] +
                                    (Number.isNaN(parts[1]) ? 0 : parts[1])) / 2
                              : parts[0] || 0;
                          const sentenceLenPct = Math.max(
                            0,
                            Math.min(
                              100,
                              Math.round((sentenceLenAvg / 80) * 100),
                            ),
                          );

                          const hsTemp =
                            getNested(cfg, 'headings_style.temperature') ||
                            getNested(cfg, 'headings.temperature') ||
                            {};
                          const aH2 = toNum(hsTemp?.assertiveness?.h2);
                          const aH3 = toNum(hsTemp?.assertiveness?.h3);
                          const assertAvg =
                            aH2 == null && aH3 == null
                              ? null
                              : clamp(
                                  Math.round(
                                    ((aH2 ?? aH3 ?? 0) + (aH3 ?? aH2 ?? 0)) / 2,
                                  ),
                                );

                          const toneIntensity = (() => {
                            const direct = toNum(
                              pickFirst(cfg, [
                                'prose_style.tone.intensity',
                                'tone.intensity',
                              ]),
                            );
                            if (direct != null) return clamp(direct);
                            if (assertAvg != null) return assertAvg;
                            const tf = pickFirst(cfg, [
                              'prose_style.transitions.frequency_per_1000w.typical',
                              'rhythm.transitions.frequency_per_1000w.typical',
                            ]);
                            if (typeof tf === 'string') {
                              const ps = tf
                                .split('-')
                                .map(x => Number(String(x).trim()))
                                .filter(n => Number.isFinite(n));
                              if (ps.length) {
                                const mid =
                                  ps.length >= 2 ? (ps[0] + ps[1]) / 2 : ps[0];
                                return clamp(Math.round(mid * 10));
                              }
                            }
                            return null;
                          })();

                          const vocabDensity = (() => {
                            const direct = toNum(
                              pickFirst(cfg, [
                                'prose_style.rhythm.info_density',
                                'rhythm.info_density',
                              ]),
                            );
                            if (direct != null) return clamp(direct);
                            if (sentenceLenAvg) {
                              const norm = Math.max(
                                20,
                                Math.min(80, sentenceLenAvg),
                              );
                              const pct =
                                Math.round(80 - ((norm - 20) / 60) * 60) + 20;
                              return clamp(pct);
                            }
                            return null;
                          })();

                          const expertise = (() => {
                            const direct = toNum(
                              pickFirst(cfg, [
                                'prose_style.tone.expertise',
                                'tone.expertise',
                              ]),
                            );
                            if (direct != null) return clamp(direct);
                            if (assertAvg != null) return assertAvg;
                            const neutrality = toNum(
                              pickFirst(cfg, [
                                'prose_style.tone.neutrality',
                                'tone.neutrality',
                              ]),
                            );
                            if (neutrality != null)
                              return clamp(100 - neutrality);
                            return null;
                          })();

                          const dm = (() => {
                            // sentence_endingsはフォーム編集対象ではないので、
                            // 元のstyle_configから取得（draftConfigには含まれない）
                            const origCfg = currentStyle?.style_config || {};
                            const ps = origCfg?.prose_style || cfg?.prose_style || {};
                            const se = ps?.sentence_endings || {};
                            const ratios = se?.ratios_per_100_sentences || {};
                            return ratios.desu_masu || null;
                          })();

                          return (
                            <>
                              <div className="style-kpis">
                                <div className="style-kpi">
                                  <div className="label">
                                    想定文字数（記事全体）
                                  </div>
                                  <div className="value">
                                    {totalPref}
                                    <span className="unit">文字</span>
                                  </div>
                                </div>
                                <div className="style-kpi">
                                  <div className="label">です/ます比率</div>
                                  <div className="value">
                                    {dm ? formatRange(dm) : '—'}
                                    <span className="unit">%</span>
                                  </div>
                                </div>
                              </div>
                              <div className="style-sep"></div>
                              <div className="style-grid">
                                <div className="head">項目</div>
                                <div className="head">H2</div>
                                <div className="head">H3</div>
                                <div className="cell left">
                                  <div className="metric">基本設置数</div>
                                </div>
                                <div className="cell">
                                  <div className="num">
                                    {h2n ? formatRange(String(h2n)) : '—'}
                                    <span className="unit">個</span>
                                  </div>
                                </div>
                                <div className="cell">
                                  <div className="num">
                                    {h3n ? formatRange(String(h3n)) : '—'}
                                    <span className="unit">個</span>
                                  </div>
                                </div>
                                <div className="cell left">
                                  <div className="metric">見出し文字数</div>
                                </div>
                                <div className="cell">
                                  <div className="num">
                                    {h2h ? formatRange(String(h2h)) : '—'}
                                    <span className="unit">文字</span>
                                  </div>
                                </div>
                                <div className="cell">
                                  <div className="num">
                                    {h3h ? formatRange(String(h3h)) : '—'}
                                    <span className="unit">文字</span>
                                  </div>
                                </div>
                                <div className="cell left">
                                  <div className="metric">
                                    見出し直下の文字数
                                  </div>
                                </div>
                                <div className="cell">
                                  <div className="num">
                                    {h2b ? formatRange(String(h2b)) : '—'}
                                    <span className="unit">文字</span>
                                  </div>
                                </div>
                                <div className="cell">
                                  <div className="num">
                                    {h3b ? formatRange(String(h3b)) : '—'}
                                    <span className="unit">文字</span>
                                  </div>
                                </div>
                              </div>
                              <div className="style-sep"></div>
                              <div className="style-bars">
                                <div className="barcard">
                                  <div className="barlabel">文の長さ</div>
                                  <Slider
                                    className="viz-slider"
                                    disabled
                                    value={sentenceLenPct}
                                    tooltip={{ open: false }}
                                  />
                                  <div className="barvalue">
                                    {sentenceLenAvg
                                      ? `${Math.round(sentenceLenAvg)} 文字/文`
                                      : '—'}
                                  </div>
                                </div>
                                <div className="barcard">
                                  <div className="barlabel">トーン</div>
                                  <Slider
                                    className="viz-slider"
                                    disabled
                                    value={toneIntensity ?? 0}
                                    tooltip={{ open: false }}
                                  />
                                  <div className="barvalue">
                                    {toneIntensity != null
                                      ? `${toneIntensity}%`
                                      : '—'}
                                  </div>
                                </div>
                                <div className="barcard">
                                  <div className="barlabel">語彙性</div>
                                  <Slider
                                    className="viz-slider"
                                    disabled
                                    value={vocabDensity ?? 0}
                                    tooltip={{ open: false }}
                                  />
                                  <div className="barvalue">
                                    {vocabDensity != null
                                      ? `${vocabDensity}%`
                                      : '—'}
                                  </div>
                                </div>
                                <div className="barcard">
                                  <div className="barlabel">専門性</div>
                                  <Slider
                                    className="viz-slider"
                                    disabled
                                    value={expertise ?? 0}
                                    tooltip={{ open: false }}
                                  />
                                  <div className="barvalue">
                                    {expertise != null ? `${expertise}%` : '—'}
                                  </div>
                                </div>
                              </div>
                              <div className="style-sep"></div>
                              {(() => {
                                const fmt =
                                  cfg?.prose_style?.formatting ||
                                  cfg?.formatting ||
                                  {};
                                const bulletsFreq = (
                                  fmt?.bullets?.frequency_per_1000w?.typical ||
                                  '0-0'
                                ).toString();
                                const tablesFreq = (
                                  fmt?.tables?.frequency_per_1000w?.typical ||
                                  '0-0'
                                ).toString();
                                return (
                                  <div
                                    className="style-aux-bars"
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(2, 1fr)',
                                      gap: 12,
                                    }}
                                  >
                                    <div className="barcard">
                                      <div className="barlabel">箇条書き</div>
                                      <div className="num">
                                        {bulletsFreq}
                                        <span className="unit">/1000字</span>
                                      </div>
                                    </div>
                                    <div className="barcard">
                                      <div className="barlabel">テーブル</div>
                                      <div className="num">
                                        {tablesFreq}
                                        <span className="unit">/1000字</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                              {(() => {
                                const desc =
                                  getNested(cfg, 'metadata.description') || '';
                                return desc ? (
                                  <div
                                    className="style-summary-card search-intent-card"
                                    style={{ marginTop: 12 }}
                                  >
                                    <div className="style-summary-text search-intent-text">
                                      {desc}
                                    </div>
                                  </div>
                                ) : null;
                              })()}
                            </>
                          );
                        })()}
                      </Card>
                      <Collapse size="small" style={{ marginTop: 12 }}>
                        <Panel header="YAMLプレビュー" key="yaml">
                          {yamlLoading ? (
                            <Spin />
                          ) : (
                            <SyntaxHighlighter
                              language="yaml"
                              style={tomorrow}
                              wrapLongLines={false}
                              customStyle={{
                                whiteSpace: 'pre',
                                overflowX: 'auto',
                                minHeight: 240,
                                maxHeight: 'calc(100vh - 260px)',
                              }}
                            >
                              {yamlPreview}
                            </SyntaxHighlighter>
                          )}
                        </Panel>
                      </Collapse>
                    </div>
                  </Col>
                </Row>
              );
            })()}
            <div
              style={{
                position: 'sticky',
                bottom: 0,
                background: '#fff',
                borderTop: '1px solid #f0f0f0',
                padding: 12,
                marginTop: 16,
                zIndex: 1,
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    currentStyle?.is_default && !isAdmin ? '1fr' : '1fr 1fr',
                  gap: 8,
                }}
              >
                <Button onClick={() => setConfigModalVisible(false)} block>
                  キャンセル
                </Button>
                {/* デフォルトスタイルは管理者のみ保存可能 */}
                {(!currentStyle?.is_default || isAdmin) && (
                  <Button
                    type="primary"
                    block
                    onClick={async () => {
                      try {
                        await configForm.validateFields();
                        // フォームから値を取得
                        const values = configForm.getFieldsValue();

                        // style_configを再構築
                        const updatedStyleConfig = buildUpdatedConfigFromForm();

                        // 更新データを構築（トップレベルフィールド + style_config）
                        const updatePayload = {
                          style_config: updatedStyleConfig,
                        };

                        // name と description もフォームにあれば含める
                        if (values.name && values.name !== currentStyle.name) {
                          updatePayload.name = values.name;
                        }
                        if (values.description !== undefined) {
                          updatePayload.description = values.description;
                        }

                        console.log(
                          '[StyleUpdate] Updating style:',
                          currentStyle.id,
                        );
                        console.log(
                          '[StyleUpdate] Update payload:',
                          updatePayload,
                        );
                        console.log('[StyleUpdate] Current style (before):', {
                          name: currentStyle.name,
                          description: currentStyle.description,
                        });

                        const res = await v2StylesApi.updateStyle(
                          currentStyle.id,
                          updatePayload,
                        );
                        console.log('[StyleUpdate] Update response:', res);
                        console.log('[StyleUpdate] Response data:', {
                          name: res.name,
                          description: res.description,
                        });

                        // スタイル一覧を再取得（確実にデータを反映）
                        await loadStyles();

                        message.success('設定を更新しました');
                        setConfigModalVisible(false);
                      } catch (e) {
                        console.error('[StyleUpdate] Update failed:', e);
                        if (e?.errorFields) return;
                        message.error(
                          e?.response?.data?.detail || '更新に失敗しました',
                        );
                      }
                    }}
                  >
                    保存する
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </Modal>

        {/* YAMLモーダル */}
        <Modal
          title="YAML形式"
          open={yamlModalVisible}
          onCancel={() => setYamlModalVisible(false)}
          footer={[
            <Button
              key="copy"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(yamlContent);
                message.success('クリップボードにコピーしました');
              }}
            >
              コピー
            </Button>,
            <Button
              key="download"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                const blob = new Blob([yamlContent], { type: 'text/yaml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${currentStyle?.name || 'style'}.yaml`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              ダウンロード
            </Button>,
            <Button key="close" onClick={() => setYamlModalVisible(false)}>
              閉じる
            </Button>,
          ]}
          width={800}
        >
          <SyntaxHighlighter language="yaml" style={tomorrow}>
            {yamlContent}
          </SyntaxHighlighter>
        </Modal>
      </div>
    </>
  );
};

export default StyleManagement;
